/* global JEELIZFACEFILTER, JeelizResizer, JeelizThreeHelper, TWEEN, THREE */
import './App.css';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import io from 'socket.io-client';
import * as nsfwjs from 'nsfwjs';
import enTranslations from './translations/en.json';
import trTranslations from './translations/tr.json';
import deTranslations from './translations/de.json';
import { logger, initDatadog, setUserContext } from './datadog';
import { MdSkipNext, MdCallEnd, MdVideocam, MdMic, MdArrowDropDown, MdSettings, MdVolumeUp, MdFilterList, MdRemoveCircle, MdGraphicEq, MdPerson } from 'react-icons/md';
import { IoChatboxEllipsesOutline, IoGameController } from 'react-icons/io5';

let THREECAMERA = null;
let ANONYMOUSOBJ3D = null;
let VIDEOMESH = null;
let isJeelizInitStarted = false;
let faceDetectionListener = null;
let cameraReadyListener = null;
let lastIsDetected = null;
let canvasStream = null;
let audioStream = null;
let maskCache = {}; // Cache for preloaded masks

// Mask configurations - each mask has its own GLTF file, positioning, display name, and image
const MASK_CONFIGS = {
  mask1: {
    displayName: 'Anonymous',
    file: '/scene3.gltf',
    image: '/masks/anonymous.svg',
    scale: { x: 0.8, y: 0.8, z: 0.8 },
    position: { x: 0, y: 0, z: 0.35 },
    rotation: { x: 0, y: 0, z: 0 }
  },
  mask2: {
    displayName: 'Venetian',
    file: '/venetian.gltf',
    image: '/masks/venetian.svg',
    scale: { x: 0.8, y: 0.8, z: 0.8 },
    position: { x: 0, y: 0.35, z: 0.4 },
    rotation: { x: 0, y: 0, z: 0 }
  },
  mask3: {
    displayName: 'CatWoman',
    file: '/catwoman.gltf',
    image: '/masks/catwoman.svg',
    scale: { x: 0.45, y: 0.45, z: 0.45 },
    position: { x: 0, y: 0, z: 0.5 },
    rotation: { x: 0, y: 0, z: 0 }
  }
};

// Trivia questions for waiting screen
const TRIVIA_QUESTIONS = {
  en: [
    { question: "What is the capital of Australia?", options: ["Sydney", "Melbourne", "Canberra", "Perth"], correct: 2 },
    { question: "How many hearts does an octopus have?", options: ["1", "2", "3", "8"], correct: 2 },
    { question: "What is the smallest country in the world?", options: ["Monaco", "Vatican City", "San Marino", "Liechtenstein"], correct: 1 },
    { question: "Which planet is known as the Red Planet?", options: ["Venus", "Mars", "Jupiter", "Saturn"], correct: 1 },
    { question: "What year did the Titanic sink?", options: ["1905", "1912", "1920", "1898"], correct: 1 },
    { question: "How many bones are in the human body?", options: ["186", "206", "226", "256"], correct: 1 },
    { question: "What is the largest ocean on Earth?", options: ["Atlantic", "Indian", "Pacific", "Arctic"], correct: 2 },
    { question: "Which animal can sleep for 3 years?", options: ["Bear", "Snail", "Sloth", "Koala"], correct: 1 },
    { question: "What is the hardest natural substance?", options: ["Gold", "Iron", "Diamond", "Platinum"], correct: 2 },
    { question: "How many players are on a soccer team?", options: ["9", "10", "11", "12"], correct: 2 },
    // New questions
    { question: "What is the largest mammal?", options: ["Elephant", "Blue Whale", "Giraffe", "Hippo"], correct: 1 },
    { question: "How many continents are there?", options: ["5", "6", "7", "8"], correct: 2 },
    { question: "What is the fastest land animal?", options: ["Lion", "Cheetah", "Horse", "Leopard"], correct: 1 },
    { question: "Which country has the most pyramids?", options: ["Egypt", "Mexico", "Sudan", "Peru"], correct: 2 },
    { question: "What is the largest desert on Earth?", options: ["Sahara", "Gobi", "Antarctica", "Arabian"], correct: 2 },
    { question: "How many teeth does an adult human have?", options: ["28", "30", "32", "34"], correct: 2 },
    { question: "What is the hottest planet in our solar system?", options: ["Mercury", "Venus", "Mars", "Jupiter"], correct: 1 },
    { question: "Which animal has the longest lifespan?", options: ["Elephant", "Tortoise", "Whale", "Parrot"], correct: 1 },
    { question: "What is the most spoken language in the world?", options: ["English", "Spanish", "Mandarin", "Hindi"], correct: 2 },
    { question: "How many colors are in a rainbow?", options: ["5", "6", "7", "8"], correct: 2 },
  ],
  tr: [
    { question: "Avustralya'nın başkenti neresidir?", options: ["Sydney", "Melbourne", "Canberra", "Perth"], correct: 2 },
    { question: "Bir ahtapotun kaç kalbi vardır?", options: ["1", "2", "3", "8"], correct: 2 },
    { question: "Dünyanın en küçük ülkesi hangisidir?", options: ["Monako", "Vatikan", "San Marino", "Lihtenştayn"], correct: 1 },
    { question: "Hangi gezegen Kızıl Gezegen olarak bilinir?", options: ["Venüs", "Mars", "Jüpiter", "Satürn"], correct: 1 },
    { question: "Titanik hangi yıl battı?", options: ["1905", "1912", "1920", "1898"], correct: 1 },
    { question: "İnsan vücudunda kaç kemik vardır?", options: ["186", "206", "226", "256"], correct: 1 },
    { question: "Dünyadaki en büyük okyanus hangisidir?", options: ["Atlantik", "Hint", "Pasifik", "Arktik"], correct: 2 },
    { question: "Hangi hayvan 3 yıl uyuyabilir?", options: ["Ayı", "Salyangoz", "Tembel Hayvan", "Koala"], correct: 1 },
    { question: "En sert doğal madde nedir?", options: ["Altın", "Demir", "Elmas", "Platin"], correct: 2 },
    { question: "Bir futbol takımında kaç oyuncu vardır?", options: ["9", "10", "11", "12"], correct: 2 },
    // Yeni sorular
    { question: "En büyük memeli hayvan hangisidir?", options: ["Fil", "Mavi Balina", "Zürafa", "Su Aygırı"], correct: 1 },
    { question: "Dünyada kaç kıta vardır?", options: ["5", "6", "7", "8"], correct: 2 },
    { question: "En hızlı kara hayvanı hangisidir?", options: ["Aslan", "Çita", "At", "Leopar"], correct: 1 },
    { question: "Hangi ülkede en çok piramit vardır?", options: ["Mısır", "Meksika", "Sudan", "Peru"], correct: 2 },
    { question: "Dünyanın en büyük çölü hangisidir?", options: ["Sahra", "Gobi", "Antarktika", "Arabistan"], correct: 2 },
    { question: "Yetişkin bir insanda kaç diş vardır?", options: ["28", "30", "32", "34"], correct: 2 },
    { question: "Güneş sistemindeki en sıcak gezegen hangisidir?", options: ["Merkür", "Venüs", "Mars", "Jüpiter"], correct: 1 },
    { question: "En uzun ömürlü hayvan hangisidir?", options: ["Fil", "Kaplumbağa", "Balina", "Papağan"], correct: 1 },
    { question: "Dünyada en çok konuşulan dil hangisidir?", options: ["İngilizce", "İspanyolca", "Mandarin", "Hintçe"], correct: 2 },
    { question: "Gökkuşağında kaç renk vardır?", options: ["5", "6", "7", "8"], correct: 2 },
  ],
  de: [
    { question: "Was ist die Hauptstadt von Australien?", options: ["Sydney", "Melbourne", "Canberra", "Perth"], correct: 2 },
    { question: "Wie viele Herzen hat ein Oktopus?", options: ["1", "2", "3", "8"], correct: 2 },
    { question: "Welches ist das kleinste Land der Welt?", options: ["Monaco", "Vatikanstadt", "San Marino", "Liechtenstein"], correct: 1 },
    { question: "Welcher Planet ist als Roter Planet bekannt?", options: ["Venus", "Mars", "Jupiter", "Saturn"], correct: 1 },
    { question: "In welchem Jahr sank die Titanic?", options: ["1905", "1912", "1920", "1898"], correct: 1 },
    { question: "Wie viele Knochen hat der menschliche Körper?", options: ["186", "206", "226", "256"], correct: 1 },
    { question: "Welcher ist der größte Ozean der Erde?", options: ["Atlantik", "Indisch", "Pazifik", "Arktis"], correct: 2 },
    { question: "Welches Tier kann 3 Jahre schlafen?", options: ["Bär", "Schnecke", "Faultier", "Koala"], correct: 1 },
    { question: "Was ist die härteste natürliche Substanz?", options: ["Gold", "Eisen", "Diamant", "Platin"], correct: 2 },
    { question: "Wie viele Spieler sind in einer Fußballmannschaft?", options: ["9", "10", "11", "12"], correct: 2 },
    // Neue Fragen
    { question: "Was ist das größte Säugetier?", options: ["Elefant", "Blauwal", "Giraffe", "Nilpferd"], correct: 1 },
    { question: "Wie viele Kontinente gibt es?", options: ["5", "6", "7", "8"], correct: 2 },
    { question: "Was ist das schnellste Landtier?", options: ["Löwe", "Gepard", "Pferd", "Leopard"], correct: 1 },
    { question: "Welches Land hat die meisten Pyramiden?", options: ["Ägypten", "Mexiko", "Sudan", "Peru"], correct: 2 },
    { question: "Was ist die größte Wüste der Erde?", options: ["Sahara", "Gobi", "Antarktis", "Arabien"], correct: 2 },
    { question: "Wie viele Zähne hat ein erwachsener Mensch?", options: ["28", "30", "32", "34"], correct: 2 },
    { question: "Was ist der heißeste Planet im Sonnensystem?", options: ["Merkur", "Venus", "Mars", "Jupiter"], correct: 1 },
    { question: "Welches Tier hat die längste Lebensdauer?", options: ["Elefant", "Schildkröte", "Wal", "Papagei"], correct: 1 },
    { question: "Was ist die meistgesprochene Sprache der Welt?", options: ["Englisch", "Spanisch", "Mandarin", "Hindi"], correct: 2 },
    { question: "Wie viele Farben hat ein Regenbogen?", options: ["5", "6", "7", "8"], correct: 2 },
  ]
};

// Get signaling URL from environment, default to localhost for development
let SIGNALING_SERVER_URL = process.env.REACT_APP_SIGNALING_URL || 'http://localhost:3001';

// If URL doesn't start with http:// or https://, add https://
if (SIGNALING_SERVER_URL && !SIGNALING_SERVER_URL.startsWith('http://') && !SIGNALING_SERVER_URL.startsWith('https://')) {
  SIGNALING_SERVER_URL = `https://${SIGNALING_SERVER_URL}`;
}

// Game questions will be loaded from the backend

// Cookie helper functions
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// Helper to get or create persistent anonymous user ID
const getOrCreateAUserId = () => {
  let aUserId = getCookie('aUserId');
  let isReturningUser = false;
  let minutesSinceCreation = 0;
  
  if (!aUserId) {
    // New user - create ID and store creation time
    if (window.crypto && window.crypto.randomUUID) {
      aUserId = window.crypto.randomUUID();
    } else {
      // Fallback for older browsers
      aUserId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
    setCookie('aUserId', aUserId, 365);
    setCookie('aUserIdCreatedAt', Date.now().toString(), 365);
    console.log('Generated new aUserId:', aUserId);
  } else {
    // Existing user - check if cookie is older than 30 minutes
    const createdAt = getCookie('aUserIdCreatedAt');
    if (createdAt) {
      minutesSinceCreation = Math.floor((Date.now() - parseInt(createdAt, 10)) / (1000 * 60));
      isReturningUser = minutesSinceCreation >= 30;
    } else {
      // Cookie exists but no creation time - set it now, assume returning
      setCookie('aUserIdCreatedAt', Date.now().toString(), 365);
      isReturningUser = true; // Assume returning since cookie existed before we tracked time
    }
    console.log('Loaded existing aUserId:', aUserId, 'minutesSinceCreation:', minutesSinceCreation, 'isReturning:', isReturningUser);
  }
  
  return { aUserId, isReturningUser, minutesSinceCreation };
};

// Country to language mapping
const countryToLanguage = {
  'Türkiye': 'tr',
  'Germany': 'de',
  'Austria': 'de',
  'Switzerland': 'de',
  // Add more mappings as needed
  // Default to 'en' for countries not in this map
};

// Get language for a country
const getLanguageForCountry = (country) => {
  return countryToLanguage[country] || 'en';
};

// Translation system - load from JSON files
const translations = {
  en: enTranslations,
  tr: trTranslations,
  de: deTranslations
};

// Translation function
const t = (key, lang = 'en', params = {}) => {
  const translation = translations[lang]?.[key] || translations.en[key] || key;
  // Replace parameters like {playerScore} with actual values
  return translation.replace(/\{(\w+)\}/g, (match, paramKey) => {
    return params[paramKey] !== undefined ? params[paramKey] : match;
  });
};

// List of countries for selection
const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Italy', 'Spain',
  'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Poland', 'Portugal', 'Greece', 'Ireland', 'Czech Republic', 'Hungary', 'Romania', 'Bulgaria',
  'Croatia', 'Slovakia', 'Slovenia', 'Estonia', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta',
  'Cyprus', 'Japan', 'South Korea', 'China', 'India', 'Singapore', 'Malaysia', 'Thailand',
  'Philippines', 'Indonesia', 'Vietnam', 'Taiwan', 'Hong Kong', 'New Zealand', 'Brazil', 'Mexico',
  'Argentina', 'Chile', 'Colombia', 'Peru', 'Venezuela', 'Ecuador', 'Uruguay', 'Paraguay',
  'South Africa', 'Egypt', 'Nigeria', 'Kenya', 'Morocco', 'Tunisia', 'Ghana', 'Israel',
  'Türkiye', 'Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Bahrain', 'Oman',
  'Russia', 'Ukraine', 'Belarus', 'Kazakhstan', 'Uzbekistan', 'Georgia', 'Armenia', 'Azerbaijan'
].sort();

function detect_callback(isDetected) {
  if (typeof faceDetectionListener === 'function') {
    faceDetectionListener(isDetected);
  }
}

// Preload mask GLTF file into cache (called early, before camera starts)
function preloadMask(maskId, retryCount = 0) {
  const config = MASK_CONFIGS[maskId];
  if (!config || maskCache[maskId]) {
    // Already cached or invalid mask
    return;
  }

  // Check if THREE is available
  if (typeof THREE === 'undefined' || !THREE.GLTFLoader) {
    console.warn('THREE.js not available yet for mask preloading');
    return;
  }

  console.log('Preloading mask:', maskId, 'from', config.file, retryCount > 0 ? `(retry ${retryCount})` : '');
  const gltfLoader = new THREE.GLTFLoader();
  gltfLoader.load(
    config.file,
    (gltf) => {
      // Cache the loaded GLTF
      maskCache[maskId] = gltf;
      console.log('Preloaded mask:', maskId);
    },
    undefined,
    (error) => {
      if (retryCount < 2) {
        // Retry up to 2 times with increasing delay
        console.warn(`⚠️ Mask preload failed, retrying in ${(retryCount + 1) * 2}s:`, maskId);
        setTimeout(() => preloadMask(maskId, retryCount + 1), (retryCount + 1) * 2000);
      } else {
        console.warn('⚠️ Mask preload failed after retries (will load on demand):', maskId, error.message || error);
        // Mask will still load when user selects it - preload is just an optimization
      }
    }
  );
}

// Function to load a mask with specific configuration
function loadMask(threeStuffs, maskId, retryCount = 0, failedMasks = []) {
  const config = MASK_CONFIGS[maskId];
  if (!config) {
    console.error('Invalid mask ID:', maskId);
    return;
  }

  // Remove old mask if exists
  if (ANONYMOUSOBJ3D) {
    if (threeStuffs.faceObject) {
      threeStuffs.faceObject.remove(ANONYMOUSOBJ3D);
    }
    ANONYMOUSOBJ3D = null;
  }

  // Check if mask is already cached (preloaded)
  if (maskCache[maskId]) {
    // Use cached GLTF
    const gltf = maskCache[maskId];
    const model = gltf.scene.clone(); // Clone the scene for this instance
    
    // Apply mask-specific configuration
    model.scale.set(config.scale.x, config.scale.y, config.scale.z);
    model.position.set(config.position.x, config.position.y, config.position.z);
    model.rotation.set(config.rotation.x, config.rotation.y, config.rotation.z);

    // IMPORTANT: disable frustum culling
    model.traverse((obj) => {
      if (obj.isMesh || obj.isSkinnedMesh) {
        obj.frustumCulled = false;
      }
    });

    ANONYMOUSOBJ3D = new THREE.Object3D();
    ANONYMOUSOBJ3D.add(model);

    threeStuffs.faceObject.add(ANONYMOUSOBJ3D);
    console.log('Loaded mask from cache:', maskId);
    return;
  }

  // Load new mask (fallback if not preloaded)
  const gltfLoader = new THREE.GLTFLoader();
  gltfLoader.load(
    config.file,
    (gltf) => {
      // Re-check and remove if a mask exists (race condition protection)
      if (ANONYMOUSOBJ3D && threeStuffs.faceObject) {
        threeStuffs.faceObject.remove(ANONYMOUSOBJ3D);
        ANONYMOUSOBJ3D = null;
      }

      const model = gltf.scene;
      if (!model) return;

      // Apply mask-specific configuration
      model.scale.set(config.scale.x, config.scale.y, config.scale.z);
      model.position.set(config.position.x, config.position.y, config.position.z);
      model.rotation.set(config.rotation.x, config.rotation.y, config.rotation.z);

      // IMPORTANT: disable frustum culling
      model.traverse((obj) => {
        if (obj.isMesh || obj.isSkinnedMesh) {
          obj.frustumCulled = false;
        }
      });

      ANONYMOUSOBJ3D = new THREE.Object3D();
      ANONYMOUSOBJ3D.add(model);

      threeStuffs.faceObject.add(ANONYMOUSOBJ3D);
      console.log('Loaded mask:', maskId, 'from', config.file);
      
      // Cache the successfully loaded mask
      maskCache[maskId] = gltf;
    },
    undefined,
    (error) => {
      console.error('Error loading mask:', maskId, error);
      
      if (retryCount < 2) {
        // Retry up to 2 times
        const delay = (retryCount + 1) * 1000; // 1s, 2s
        console.warn(`⚠️ Retrying mask load in ${delay/1000}s... (${retryCount + 1}/2)`);
        setTimeout(() => loadMask(threeStuffs, maskId, retryCount + 1, failedMasks), delay);
      } else {
        // All retries failed - try fallback to another mask
        console.error(`❌ Failed to load mask ${maskId} after ${retryCount + 1} attempts`);
        
        // Track failed masks to avoid infinite loops
        failedMasks.push(maskId);
        
        // Try to fallback to a different mask
        const availableMasks = Object.keys(MASK_CONFIGS).filter(id => !failedMasks.includes(id));
        
        if (availableMasks.length > 0) {
          const fallbackMask = availableMasks[0];
          console.log(`🔄 Falling back to ${fallbackMask} mask...`);
          logger.warn('Mask loading failed, using fallback', {
            event: 'mask_load_failed_fallback',
            failedMask: maskId,
            fallbackMask: fallbackMask,
            error: error.message,
          });
          loadMask(threeStuffs, fallbackMask, 0, failedMasks);
        } else {
          console.error('❌ No working masks available');
          logger.error('All masks failed to load', new Error('All mask loading attempts failed'), {
            event: 'all_masks_failed',
            failedMasks: failedMasks,
          });
        }
      }
    }
  );
}

function init_threeScene(spec, initialMaskId = 'mask1') {
  const threeStuffs = JeelizThreeHelper.init(spec, detect_callback);
  VIDEOMESH = threeStuffs.videoMesh || null;

  // Store threeStuffs globally for mask switching
  if (typeof window !== 'undefined') {
    window.threeStuffs = threeStuffs;
  }

  // Load initial mask
  loadMask(threeStuffs, initialMaskId);

  // camera & lights
  THREECAMERA = JeelizThreeHelper.create_camera();
  if (typeof window !== 'undefined') {
    window.THREECAMERA = THREECAMERA;
  }

  const ambient = new THREE.AmbientLight(0xffffff, 0.8);
  threeStuffs.scene.add(ambient);

  const dirLight = new THREE.DirectionalLight(0xffffff, 0.5);
  dirLight.position.set(100, 1000, 1000);
  threeStuffs.scene.add(dirLight);
}

function init_faceFilter(videoSettings, canvasId, retryCount = 0) {
  const maxRetries = 3;
  
  JEELIZFACEFILTER.init({
    canvasId,
    // Neural network model options (from lightest to most accurate):
    // NN_VERYLIGHT_1.json - Lightest (lowest resources, lowest accuracy)
    // NN_LIGHT_1.json - Light (low resources, moderate accuracy)
    // NN_STANDARD_1.json - Standard (balanced, better accuracy) ← CURRENT
    // NN_STANDARD_2.json - Standard v2 (better accuracy)
    // NN_STANDARD_3.json - Standard v3 (even better accuracy)
    // NN_DEFAULT.json - Default (best accuracy, higher resources)
    NNCPath: '/jeeliz/neuralNets/NN_STANDARD_1.json', // Upgraded from NN_LIGHT_1 for better detection
    videoSettings,
    // Performance optimizations (kept for resource efficiency)
    followZRot: false, // Disable full rotation tracking (reduces computation)
    maxFacesDetected: 1, // Detect up to 3 faces (default is 1)
    animateDelay: 0, // Keep at 0 for smoothness, or increase to 16-33ms for lower CPU
    // Scan settings - optimized for better detection with STANDARD model
    scanSettings: {
      threshold: 1.2, // Detection threshold (higher = less sensitive, faster)
      nScaleLevels: 2, // Increased from 1 to 2 for better detection (STANDARD model can handle it)
      scale0Factor: 1, // Lower value = wider field of view, less zoom (0.6 shows more of head/shoulders)
      nDetectsPerLoopRange: [1, 3], // Increased from [1,3] to [2,4] for better detection
    },
    callbackReady: (errCode, spec) => {
      if (errCode) {
        // eslint-disable-next-line no-console
        console.error('JEELIZFACEFILTER ERROR =', errCode);
        
        if (retryCount < maxRetries) {
          const delay = (retryCount + 1) * 2000; // 2s, 4s, 6s
          console.warn(`⚠️ Jeeliz initialization failed, retrying in ${delay/1000}s... (attempt ${retryCount + 1}/${maxRetries})`);
          isJeelizInitStarted = false; // Reset flag to allow retry
          setTimeout(() => {
            init_faceFilter(videoSettings, canvasId, retryCount + 1);
          }, delay);
          return;
        }
        
        // All retries failed
        logger.error('JEELIZFACEFILTER initialization failed after retries', new Error(`Error code: ${errCode}`), { 
          errCode,
          retries: maxRetries,
        });
        console.error(`❌ Jeeliz failed after ${maxRetries} attempts. Neural network file may be unavailable (503 error).`);
        // Notify that camera failed to initialize
        if (typeof cameraReadyListener === 'function') {
          cameraReadyListener(false);
        }
        return;
      }
      
      console.log('✅ Jeeliz face tracking initialized successfully');
      init_threeScene(spec);
      
      // Check what resolution the camera actually provided and match canvas to it
      if (spec.video && spec.video.srcObject) {
        const videoTrack = spec.video.srcObject.getVideoTracks()[0];
        if (videoTrack) {
          const actualSettings = videoTrack.getSettings();
          console.log('📹 Camera actually provided:', {
            width: actualSettings.width,
            height: actualSettings.height,
            aspectRatio: actualSettings.aspectRatio,
            frameRate: actualSettings.frameRate
          });
          
          // Match canvas resolution to actual video resolution
          const canvas = document.getElementById(canvasId);
          if (canvas && actualSettings.width && actualSettings.height) {
            canvas.width = actualSettings.width;
            canvas.height = actualSettings.height;
            console.log(`📹 Canvas resolution matched to video: ${actualSettings.width}x${actualSettings.height}`);
          }
        }
      }
      
      // Capture canvas stream for WebRTC at maximum quality
      const canvas = document.getElementById(canvasId);
      if (canvas) {
        // Use 20 FPS instead of 30 FPS for better performance (still smooth)
        canvasStream = canvas.captureStream(20); // 20 FPS
        
        // Notify that camera is ready
        if (typeof cameraReadyListener === 'function') {
          cameraReadyListener(true);
        }
        
        // Get microphone audio directly
        navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false
        })
          .then((micStream) => {
            audioStream = micStream;
            // Add audio track to canvas stream
            const audioTracks = micStream.getAudioTracks();
            if (audioTracks.length > 0) {
              audioTracks.forEach(track => {
                canvasStream.addTrack(track);
              });
              console.log('Audio track added to stream');
            }
          })
          .catch((err) => {
            console.error('Error getting microphone:', err);
            // Fallback: try to get audio from video element
            if (spec.video && spec.video.srcObject) {
              const audioTracks = spec.video.srcObject.getAudioTracks();
              audioTracks.forEach(track => {
                canvasStream.addTrack(track);
              });
            }
          });
      }
    },
    callbackTrack: (detectState) => {
      const isDetected = JeelizThreeHelper.get_isDetected
        ? JeelizThreeHelper.get_isDetected()
        : true;

      if (VIDEOMESH) {
        VIDEOMESH.visible = isDetected;
      }

      if (
        typeof faceDetectionListener === 'function' &&
        isDetected !== lastIsDetected
      ) {
        lastIsDetected = isDetected;
        faceDetectionListener(!!isDetected);
      }

      if (typeof TWEEN !== 'undefined') {
        TWEEN.update();
      }

      JeelizThreeHelper.render(detectState, THREECAMERA);
    },
  });
}

function initJeelizAnonymous(canvasId = 'jeeFaceFilterCanvas') {
  if (
    typeof JEELIZFACEFILTER === 'undefined' ||
    typeof JeelizResizer === 'undefined' ||
    typeof JeelizThreeHelper === 'undefined' ||
    typeof THREE === 'undefined'
  ) {
    // eslint-disable-next-line no-console
    console.error(
      'Jeeliz / Three.js scripts are not loaded. Check script tags in public/index.html.',
    );
    return;
  }

  // Check if canvas element exists
  const canvas = document.getElementById(canvasId);
  if (!canvas) {
    console.warn(`Canvas element with id "${canvasId}" not found. Camera initialization skipped.`);
    isJeelizInitStarted = false; // Reset flag so it can be retried
    return;
  }

  if (isJeelizInitStarted) return;
  isJeelizInitStarted = true;

  // First, check camera capabilities to determine what resolutions are actually supported
  const checkCameraCapabilities = async () => {
    try {
      // Get a temporary stream to check capabilities
      const tempStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      });
      
      const videoTrack = tempStream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities();
      const settings = videoTrack.getSettings();
      
      // Stop the temporary stream
      tempStream.getTracks().forEach(track => track.stop());
      
      console.log('📹 Camera capabilities:', capabilities);
      console.log('📹 Camera settings:', settings);
      
      // Use capabilities to determine optimal constraints
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;
      const isPortrait = window.innerHeight > window.innerWidth;
      
      // Get available width/height ranges from capabilities
      const widthRange = capabilities.width || {};
      const heightRange = capabilities.height || {};
      const aspectRatioRange = capabilities.aspectRatio || {};
      
      console.log('📹 Available width range:', widthRange);
      console.log('📹 Available height range:', heightRange);
      console.log('📹 Available aspect ratio range:', aspectRatioRange);
      
      // Use minimal constraints - just prevent zoom by capping max resolution
      // Let the camera use its native/default resolution for best quality
      // Only set max constraints to prevent camera from zooming to fill high resolutions
      const capabilityBasedSettings = {
        facingMode: 'user',
        // Only set max to prevent zoom - let camera choose its native resolution
        // Cap at reasonable values to prevent zoom/crop issues
        maxWidth: isMobile && !isTablet ? 1280 : 1920,
        maxHeight: isMobile && !isTablet ? 1280 : 1080,
        // Don't set ideal/min - let camera use what it wants
      };
      
      console.log('📹 Using minimal constraints - letting camera choose native resolution');
      
      console.log('📹 Using camera-capability-based video settings:', capabilityBasedSettings);
      
      // Now initialize Jeeliz - it will handle canvas sizing based on video
      JeelizResizer.size_canvas({
        canvasId,
        overSamplingFactor: 1,
        callback: (isError, bestVideoSettings) => {
          if (isError) {
            console.error('JeelizResizer error:', isError);
            isJeelizInitStarted = false;
            return;
          }
          
          // Merge JeelizResizer suggestions with our minimal constraints
          // JeelizResizer will suggest based on canvas size, we just add max constraints to prevent zoom
          const finalVideoSettings = {
            ...bestVideoSettings,
            ...capabilityBasedSettings, // Add max constraints to prevent zoom
          };
          
          console.log('📹 Final video settings (minimal constraints):', finalVideoSettings);
          init_faceFilter(finalVideoSettings, canvasId);
        },
      });
      
    } catch (error) {
      console.warn('⚠️ Could not check camera capabilities, using fallback settings:', error);
      
      // Fallback to previous logic if capability check fails
      JeelizResizer.size_canvas({
        canvasId,
        overSamplingFactor: 1,
        callback: (isError, bestVideoSettings) => {
          if (isError) {
            console.error('JeelizResizer error:', isError);
            isJeelizInitStarted = false;
            return;
          }
          
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;
          const isPortrait = window.innerHeight > window.innerWidth;
          
          const canvas = document.getElementById(canvasId);
          if (canvas) {
            let targetWidth, targetHeight;
            if (isMobile && !isTablet) {
              targetWidth = isPortrait ? 640 : 1280;
              targetHeight = isPortrait ? 1136 : 720;
            } else {
              targetWidth = 1920;
              targetHeight = 1080;
            }
            canvas.width = targetWidth;
            canvas.height = targetHeight;
          }
          
          const maxVideoSettings = {
            idealWidth: isMobile && !isTablet ? (isPortrait ? 640 : 1280) : 1920,
            idealHeight: isMobile && !isTablet ? (isPortrait ? 1136 : 720) : 1080,
            maxWidth: isMobile && !isTablet ? (isPortrait ? 640 : 1280) : 1920,
            maxHeight: isMobile && !isTablet ? (isPortrait ? 1136 : 720) : 1080,
            minWidth: isMobile && !isTablet ? (isPortrait ? 480 : 640) : 1280,
            minHeight: isMobile && !isTablet ? (isPortrait ? 640 : 480) : 720,
            facingMode: 'user',
            ...bestVideoSettings,
          };
          
          init_faceFilter(maxVideoSettings, canvasId);
        },
      });
    }
  };
  
  // Start by checking camera capabilities
  checkCameraCapabilities();
}

function App() {
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected
  const [isCameraReady, setIsCameraReady] = useState(false);
  // Track loading state for mask images
  const [maskImageLoading, setMaskImageLoading] = useState(() => {
    const loading = {};
    Object.keys(MASK_CONFIGS).forEach(maskId => {
      loading[maskId] = true; // Start with all masks as loading
    });
    return loading;
  });
  // Always initialize with Türkiye as default country
  const [selectedCountry, setSelectedCountry] = useState('Türkiye');
  const [cameraPermissionGranted, setCameraPermissionGranted] = useState(false);
  const [shouldAutoJoin, setShouldAutoJoin] = useState(false);
  const [isRequestingCameraPermission, setIsRequestingCameraPermission] = useState(false);
  const [showAttentionButton, setShowAttentionButton] = useState(false);
  const [showFloatingHint, setShowFloatingHint] = useState(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
  const [denialCooldownSeconds, setDenialCooldownSeconds] = useState(0);
  const [detectedInAppBrowser, setDetectedInAppBrowser] = useState(null);
  
  // Waiting screen enhancements
  const [showTriviaPrompt, setShowTriviaPrompt] = useState(false);
  const [showTrivia, setShowTrivia] = useState(false);
  const [triviaScore, setTriviaScore] = useState(0);
  const [currentTriviaIndex, setCurrentTriviaIndex] = useState(0);
  const [triviaAnswer, setTriviaAnswer] = useState(null); // null, 'correct', 'wrong'
  const waitingStartTimeRef = useRef(null);
  const [showFAQ, setShowFAQ] = useState(false);
  
  // Device selection states
  const [availableVideoDevices, setAvailableVideoDevices] = useState([]);
  const [availableAudioDevices, setAvailableAudioDevices] = useState([]);
  const [availableAudioOutputDevices, setAvailableAudioOutputDevices] = useState([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState(null);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState(null);
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState(null);
  const [noiseCancellationEnabled, setNoiseCancellationEnabled] = useState(true);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const [settingsTab, setSettingsTab] = useState('masks'); // 'masks', 'audio' or 'video'
  const [selectedMask, setSelectedMask] = useState('mask1'); // Default to first mask (venetian)
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nsfwDetected, setNsfwDetected] = useState(false);
  const [nsfwModel, setNsfwModel] = useState(null);
  const nsfwModelRef = useRef(null); // Ref to track model for closures
  const nsfwDetectedRef = useRef(false); // Ref to track NSFW detection state for closures
  
  // Determine language based on selected country
  const currentLanguage = getLanguageForCountry(selectedCountry);

  // Preload mask when page loads (before camera starts)
  useEffect(() => {
    // Wait for THREE.js to be available (it's loaded via script tag)
    const checkAndPreload = () => {
      if (typeof THREE !== 'undefined' && THREE.GLTFLoader) {
        // Preload the default mask (mask1) or selected mask
        const maskToPreload = selectedMask || 'mask1';
        preloadMask(maskToPreload);
      } else {
        // THREE.js not ready yet, try again after a short delay
        setTimeout(checkAndPreload, 100);
      }
    };
    
    checkAndPreload();
  }, []); // Only run once on mount

  // Preload mask when selectedMask changes
  useEffect(() => {
    if (typeof THREE !== 'undefined' && THREE.GLTFLoader && selectedMask) {
      preloadMask(selectedMask);
    }
  }, [selectedMask]);
  
  // Game state
  const [gameState, setGameState] = useState('idle'); // idle, requested, active, finished
  const [gameRequestedBy, setGameRequestedBy] = useState(null); // 'self' or 'peer'
  const [showGameSelection, setShowGameSelection] = useState(false); // Show game selection menu
  const [currentGameType, setCurrentGameType] = useState(null); // 'quiz' or 'thisOrThat'
  const [gameQuestions, setGameQuestions] = useState([]); // Questions loaded from backend
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [playerScore, setPlayerScore] = useState(0);
  const [peerScore, setPeerScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showAnswerResult, setShowAnswerResult] = useState(false);
  const [peerAnswered, setPeerAnswered] = useState(false);
  const [bothAnswered, setBothAnswered] = useState(false);
  const [nextQuestionCountdown, setNextQuestionCountdown] = useState(null); // Countdown for next question (3, 2, 1)
  const selectedAnswerRef = useRef(null); // Ref to track selected answer for synchronization
  const gameQuestionsRef = useRef([]); // Ref to track current game questions for socket handlers
  const questionIndexRef = useRef(0); // Ref to track current question index for socket handlers
  const peerFinalAnswerRef = useRef(null); // Ref to track peer's final answer index for the current question
  const scoresUpdatedRef = useRef(false); // Ref to track if scores have been updated for the current question
  
  // Mask removal request state
  const [maskRemovalState, setMaskRemovalState] = useState('idle'); // idle, requested
  const [maskRemovalRequestedBy, setMaskRemovalRequestedBy] = useState(null); // 'self' or 'peer'
  const [masksRemoved, setMasksRemoved] = useState(false); // Track if masks have been removed after acceptance
  const previousMaskRef = useRef(null); // Store mask before removal to restore on disconnect/skip
  
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const chatEndRef = useRef(null);
  const detectionCanvasRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const showChatRef = useRef(false); // Ref to track chat panel state for socket callbacks
  const selectedCountryRef = useRef(selectedCountry); // Ref to track selected country for socket callbacks
  const selectedMaskRef = useRef(selectedMask); // Ref to track selected mask for socket callbacks
  const currentGameTypeRef = useRef(currentGameType); // Ref to track current game type for socket callbacks
  
  // Progressive hints state (replaces old onboarding tour)
  const [activeHint, setActiveHint] = useState(null); // { id, target, content, emoji }
  const [hintPosition, setHintPosition] = useState(null);
  const shownHintsRef = useRef(new Set()); // Track which hints have been shown this session
  const connectedTimeRef = useRef(0); // Track how long user has been connected
  const hintTimersRef = useRef([]); // Store timer IDs for cleanup
  
  // Progressive hint configurations
  const progressiveHints = useMemo(() => ({
    game: {
      id: 'game',
      target: '.control-button.game-button',
      content: t('hintGame', currentLanguage) || '💡 Play a fun game together!',
      emoji: '🎮',
      delay: 10000, // 10 seconds after connected
    },
    maskRemoval: {
      id: 'maskRemoval',
      target: '.control-button.mask-removal-button',
      content: t('hintMaskRemoval', currentLanguage) || '💡 Want to see each other? Remove masks!',
      emoji: '🎭',
      delay: 60000, // 60 seconds after connected
    },
  }), [currentLanguage]);
  
  // Show a progressive hint
  const showProgressiveHint = useCallback((hintId) => {
    const hint = progressiveHints[hintId];
    if (!hint || shownHintsRef.current.has(hintId)) return;
    
    // Check if user has seen this hint before (persisted)
    const seenHints = JSON.parse(localStorage.getItem('videonext-seen-hints') || '[]');
    if (seenHints.includes(hintId)) return;
    
    const targetElement = document.querySelector(hint.target);
    if (!targetElement) return;
    
            const rect = targetElement.getBoundingClientRect();
    setHintPosition({
      top: rect.top,
              left: rect.left + rect.width / 2,
              width: rect.width,
              height: rect.height,
    });
    setActiveHint(hint);
    shownHintsRef.current.add(hintId);
    
    // Auto-dismiss after 5 seconds
    const dismissTimer = setTimeout(() => {
      dismissHint(hintId);
    }, 5000);
    hintTimersRef.current.push(dismissTimer);
  }, [progressiveHints]);
  
  // Dismiss a hint and mark as seen
  const dismissHint = useCallback((hintId) => {
    setActiveHint(null);
    setHintPosition(null);
    
    // Persist that user has seen this hint
    const seenHints = JSON.parse(localStorage.getItem('videonext-seen-hints') || '[]');
    if (!seenHints.includes(hintId)) {
      seenHints.push(hintId);
      localStorage.setItem('videonext-seen-hints', JSON.stringify(seenHints));
    }
  }, []);
  
  // Schedule progressive hints when connected
  useEffect(() => {
    if (connectionStatus === 'connected') {
      // Clear any existing timers
      hintTimersRef.current.forEach(clearTimeout);
      hintTimersRef.current = [];
      connectedTimeRef.current = Date.now();
      
      // Schedule hints based on delay
      Object.values(progressiveHints).forEach(hint => {
        const timer = setTimeout(() => {
          // Only show if still connected
          if (connectionStatus === 'connected') {
            showProgressiveHint(hint.id);
          }
        }, hint.delay);
        hintTimersRef.current.push(timer);
      });
    } else {
      // Clear timers when disconnected
      hintTimersRef.current.forEach(clearTimeout);
      hintTimersRef.current = [];
      setActiveHint(null);
      setHintPosition(null);
    }
    
    return () => {
      hintTimersRef.current.forEach(clearTimeout);
      hintTimersRef.current = [];
    };
  }, [connectionStatus, progressiveHints, showProgressiveHint]);

  // WebRTC configuration
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  const scrollChatToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle noise cancellation toggle
  const toggleNoiseCancellation = (e) => {
    const newState = e.target.checked;
    setNoiseCancellationEnabled(newState);
    
    if (audioStream) {
      const audioTracks = audioStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.applyConstraints({
          echoCancellation: newState,
          noiseSuppression: newState,
          autoGainControl: newState
        }).then(() => {
          console.log(`Microphone noise cancellation ${newState ? 'enabled' : 'disabled'}`);
        }).catch(err => {
          console.error('Error applying audio constraints:', err);
        });
      });
    }
  };

  // Handle mask selection change
  const handleMaskChange = (maskId) => {
    const previousMask = selectedMask;
    setSelectedMask(maskId);
    selectedMaskRef.current = maskId; // Keep ref in sync
    
    // Log mask change during waiting (for analytics)
    if (connectionStatus === 'connecting' && maskId !== null) {
      logger.info('Mask changed while waiting', {
        event: 'mask_changed_during_wait',
        previousMask,
        newMask: maskId,
        maskName: MASK_CONFIGS[maskId]?.displayName,
        timestamp: Date.now()
      });
    }
    
    // If user manually selects a mask (not null), clear the stored previous mask
    if (maskId !== null) {
      previousMaskRef.current = null;
    }
    
    // Reload mask if threeStuffs is available
    if (typeof window !== 'undefined' && window.threeStuffs) {
      if (maskId === null) {
        // Remove mask
        if (ANONYMOUSOBJ3D && window.threeStuffs.faceObject) {
          window.threeStuffs.faceObject.remove(ANONYMOUSOBJ3D);
          ANONYMOUSOBJ3D = null;
          console.log('Mask removed');
        }
      } else {
        loadMask(window.threeStuffs, maskId);
      }
    }
  };

  // Restore mask when disconnecting/skipping
  const restoreMask = () => {
    // Always restore mask when video chat state changes (disconnect/skip)
    // Ensure both users get their masks back - mask should never be null after state change
    // Use ref to get current mask value (avoids stale closure issue in socket handlers)
    const currentMask = selectedMaskRef.current;
    
    let maskToRestore;
    
    if (currentMask === null) {
      // Mask was removed - restore from stored previous mask or default to mask1
      maskToRestore = previousMaskRef.current || 'mask1';
      console.log('🔄 Restoring removed mask after state change:', maskToRestore, 'for user');
      handleMaskChange(maskToRestore);
    } else if (!MASK_CONFIGS[currentMask]) {
      // Invalid mask - restore to stored previous mask or default
      maskToRestore = previousMaskRef.current || 'mask1';
      console.log('🔄 Restoring invalid mask:', maskToRestore);
      handleMaskChange(maskToRestore);
    } else {
      // Mask is valid - keep it active
      console.log('✅ Mask already active:', currentMask);
      // Still clear stored mask to reset state
    }
    
    // Clear the stored mask after restoring
    previousMaskRef.current = null;
    setMasksRemoved(false); // Reset mask removal state when restoring
  };

  // Handle mask removal request
  const handleRequestMaskRemoval = () => {
    if (socketRef.current && socketRef.current.peerId && !masksRemoved) {
      logger.info('Sending mask removal request', { peerId: socketRef.current.peerId });
      socketRef.current.emit('mask-removal-request', { 
        target: socketRef.current.peerId
      });
      setMaskRemovalState('requested');
      setMaskRemovalRequestedBy('self');
      const lang = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('maskRemovalRequestSent', lang) }]);
    }
  };

  const handleAcceptMaskRemoval = () => {
    if (socketRef.current && socketRef.current.peerId) {
      logger.info('Accepting mask removal request', { peerId: socketRef.current.peerId });
      socketRef.current.emit('mask-removal-accept', { 
        target: socketRef.current.peerId
      });
      // Store current mask before removal - use ref for consistency
      // Always store something (default to mask1 if null somehow)
      const currentMask = selectedMaskRef.current || selectedMask || 'mask1';
      previousMaskRef.current = currentMask;
      console.log('💾 Stored mask before removal (acceptor):', previousMaskRef.current);
      // Remove mask for both users
      handleMaskChange(null);
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
      setMasksRemoved(true); // Mark that masks have been removed
    }
  };

  const handleDeclineMaskRemoval = () => {
    if (socketRef.current && socketRef.current.peerId) {
      logger.info('Declining mask removal request', { peerId: socketRef.current.peerId });
      socketRef.current.emit('mask-removal-decline', { target: socketRef.current.peerId });
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
    }
  };

  useEffect(() => {
    scrollChatToBottom();
  }, [chatMessages]);

  // Keep showChatRef in sync with showChat state
  useEffect(() => {
    showChatRef.current = showChat;
  }, [showChat]);

  // Keep selectedCountryRef in sync with selectedCountry state
  useEffect(() => {
    selectedCountryRef.current = selectedCountry;
  }, [selectedCountry]);

  // Keep currentGameTypeRef in sync with currentGameType state
  useEffect(() => {
    currentGameTypeRef.current = currentGameType;
  }, [currentGameType]);

  // Keep selectedMaskRef in sync with selectedMask state
  useEffect(() => {
    selectedMaskRef.current = selectedMask;
  }, [selectedMask]);

  // Start NSFW detection on remote video
  const startNsfwDetection = (intervalMs = 10000) => {
    // Clear any existing interval
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    // Create canvas for detection if it doesn't exist
    // Using size 224 for mobilenet_v2_mid model
    if (!detectionCanvasRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = 224;
      canvas.height = 224;
      detectionCanvasRef.current = canvas;
    }

    // Create downscaled intermediate canvas for efficient processing
    // This reduces CPU/GPU load by processing fewer pixels
    if (!detectionCanvasRef.current.downscaleCanvas) {
      const downscaleCanvas = document.createElement('canvas');
      downscaleCanvas.width = 320;  // Low resolution for processing (much faster)
      downscaleCanvas.height = 240;
      detectionCanvasRef.current.downscaleCanvas = downscaleCanvas;
      detectionCanvasRef.current.downscaleCtx = downscaleCanvas.getContext('2d');
    }

    console.log(`🔍 Starting NSFW detection interval (${intervalMs}ms)`);
    console.log('Model loaded:', !!nsfwModel);
    console.log('Remote video exists:', !!remoteVideoRef.current);
    console.log('Remote video paused:', remoteVideoRef.current?.paused);
    console.log('NSFW already detected:', nsfwDetected);

    // Run detection with specified interval (default 10 seconds, 5 seconds when NSFW detected)
    detectionIntervalRef.current = setInterval(() => {
      const hasVideo = !!remoteVideoRef.current;
      // Use ref to get current model value (avoids closure stale value issue)
      const hasModel = !!nsfwModelRef.current;
      const isPlaying = remoteVideoRef.current && !remoteVideoRef.current.paused;
      
      // Continue monitoring even if NSFW was previously detected (to allow recovery)
      // Debug log every interval check (only log when skipped to reduce noise)
      if (!hasVideo || !hasModel || !isPlaying) {
        return;
      }

      const video = remoteVideoRef.current;
      const canvas = detectionCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const downscaleCanvas = canvas.downscaleCanvas;
      const downscaleCtx = canvas.downscaleCtx;

      try {
        // Optimization: First draw to downscaled canvas (much faster)
        // This reduces the amount of pixels we need to process
        downscaleCtx.drawImage(video, 0, 0, downscaleCanvas.width, downscaleCanvas.height);
        // Then scale down to detection canvas size (224x224)
        ctx.drawImage(downscaleCanvas, 0, 0, canvas.width, canvas.height);
        
        // Run NSFW detection on the downscaled image
        // Use ref to get current model value
        const model = nsfwModelRef.current;
        if (!model) {
          return;
        }
        model.classify(canvas)
          .then((predictions) => {
            console.log('✅ NSFW predictions:', predictions);
            // Check for NSFW categories: Porn, Hentai, Sexy
            const nsfwCategories = ['Porn', 'Hentai', 'Sexy'];
            const nsfwPrediction = predictions.find(p => 
              nsfwCategories.includes(p.className) && p.probability > 0.7
            );

            if (nsfwPrediction) {
              console.warn('⚠️ NSFW content detected:', nsfwPrediction);
              setNsfwDetected(true);
              nsfwDetectedRef.current = true;
              // Increase detection frequency to 5 seconds when NSFW is detected
              if (intervalMs === 10000) {
                console.log('⏱️ Increasing detection frequency to 5 seconds');
                startNsfwDetection(5000);
              }
            } else {
              // No NSFW content detected - check if we need to recover
              if (nsfwDetectedRef.current) {
                console.log('✅ NSFW content no longer detected - recovering video');
                setNsfwDetected(false);
                nsfwDetectedRef.current = false;
                // Return to normal 10 second interval when NSFW is cleared
                if (intervalMs === 5000) {
                  console.log('⏱️ Returning to normal detection frequency (10 seconds)');
                  startNsfwDetection(10000);
                }
              }
            }
          })
          .catch((err) => {
            console.error('❌ Error in NSFW detection:', err);
          });
      } catch (err) {
        console.error('❌ Error drawing video to canvas:', err);
      }
    }, intervalMs);
  };

  // Initialize WebRTC peer connection
  const createPeerConnection = () => {
    // Close existing connection if any
    if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
      } catch (e) {
        console.warn('Error closing existing peer connection:', e);
      }
    }

    const pc = new RTCPeerConnection(rtcConfig);

    // Add local stream (canvas with mask) to peer connection
    if (canvasStream) {
      const tracks = canvasStream.getTracks();
      console.log('Adding tracks to peer connection:', tracks.map(t => ({ kind: t.kind, enabled: t.enabled, id: t.id })));
      tracks.forEach(track => {
        const sender = pc.addTrack(track, canvasStream);
        
        // Set adaptive quality constraints for video track
        // WebRTC will automatically adjust based on network conditions
        if (track.kind === 'video' && sender && sender.setParameters) {
          // Wait for transceiver to be ready
          setTimeout(() => {
            try {
              const params = sender.getParameters();
              if (params && params.encodings && params.encodings[0]) {
                // Maximum quality settings - adaptive based on device
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;
                
                if (isMobile && !isTablet) {
                  // Mobile phones: Higher bitrate for better quality
                  params.encodings[0].maxBitrate = 5000000; // 5 Mbps max (high quality)
                  params.encodings[0].minBitrate = 1000000;   // 1 Mbps min (good quality)
                } else {
                  // Tablets and Desktop: Maximum quality
                  params.encodings[0].maxBitrate = 10000000; // 10 Mbps max (very high quality)
                  params.encodings[0].minBitrate = 2000000; // 2 Mbps min (good quality)
                }
                params.encodings[0].maxFramerate = 30;     // 30 FPS (matches canvas capture rate for smooth video)
                
                // Enable adaptive resolution scaling
                // WebRTC will automatically scale down resolution if bandwidth is low
                // scaleResolutionDownBy = 1 means no scaling, but WebRTC can still adapt
                params.encodings[0].scaleResolutionDownBy = 1; // Start at full resolution
                
                // Enable adaptive degradation preference
                // 'balanced' = WebRTC will balance quality and framerate
                // 'maintain-resolution' = prefer reducing framerate over resolution
                // 'maintain-framerate' = prefer reducing resolution over framerate
                sender.setParameters(params).catch(err => {
                  console.warn('Could not set video parameters:', err);
                });
                
                // Set degradation preference for adaptive quality
                // This tells WebRTC how to adapt when bandwidth is limited
                if (sender.setParameters) {
                  // Use 'balanced' for best adaptive behavior
                  // WebRTC will reduce both resolution and framerate as needed
                  const adaptiveParams = sender.getParameters();
                  if (adaptiveParams && adaptiveParams.degradationPreference !== undefined) {
                    adaptiveParams.degradationPreference = 'balanced';
                    sender.setParameters(adaptiveParams).catch(err => {
                      console.warn('Could not set degradation preference:', err);
                    });
                  }
                }
              }
            } catch (err) {
              console.warn('Could not optimize video quality:', err);
            }
          }, 100);
        }
      });
    } else {
      console.warn('Canvas stream not available when creating peer connection');
    }

    // Handle remote stream
    pc.ontrack = (event) => {
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
        // Ensure audio plays and is not muted
        remoteVideoRef.current.muted = false;
        remoteVideoRef.current.play().catch(err => {
          console.error('Error playing remote video:', err);
        });
        console.log('Remote stream received, audio tracks:', event.streams[0].getAudioTracks().length);
        
        // Reset NSFW detection when new stream starts
        setNsfwDetected(false);
        nsfwDetectedRef.current = false;
        
        // Wait for video to be ready, then start detection
        if (remoteVideoRef.current) {
          const video = remoteVideoRef.current;
          const startDetection = () => {
            console.log('🎬 Video ready, checking if detection can start...');
            // Use ref to get current model value (avoids closure stale value issue)
            const model = nsfwModelRef.current;
            console.log('  - nsfwModel exists:', !!model);
            console.log('  - video.paused:', video.paused);
            console.log('  - video.readyState:', video.readyState);
            
            // Use a small delay to ensure video is actually playing
            setTimeout(() => {
              // Check nsfwModel from ref (always current value)
              if (model && !video.paused && video.readyState >= 2) {
                console.log('✅ Starting NSFW detection...');
                startNsfwDetection();
              } else {
                console.log('⏸️ Cannot start detection yet:', {
                  hasModel: !!model,
                  isPlaying: !video.paused,
                  readyState: video.readyState
                });
                // Try again after a short delay (check ref again)
                setTimeout(() => {
                  if (nsfwModelRef.current && !video.paused && video.readyState >= 2) {
                    console.log('✅ Starting NSFW detection (retry)...');
                    startNsfwDetection();
                  }
                }, 500);
              }
            }, 500);
          };
          
          if (video.readyState >= 2) {
            // Video already loaded
            startDetection();
          } else {
            video.onloadedmetadata = startDetection;
            // Also try after a delay in case onloadedmetadata doesn't fire
            setTimeout(startDetection, 1000);
          }
        }
      }
    };

    // Track ICE candidates for TURN analysis
    const iceCandidates = { host: 0, srflx: 0, relay: 0 };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        // Track candidate types
        const candidateType = event.candidate.type || 'unknown';
        if (candidateType === 'host') iceCandidates.host++;
        else if (candidateType === 'srflx') iceCandidates.srflx++;
        else if (candidateType === 'relay') iceCandidates.relay++;
        
        console.log('ICE candidate:', {
          type: candidateType,
          protocol: event.candidate.protocol,
          address: event.candidate.address
        });
        
        if (socketRef.current) {
        socketRef.current.emit('ice-candidate', {
          target: socketRef.current.peerId,
          candidate: event.candidate
          });
        }
      } else {
        // ICE gathering complete
        console.log('ICE gathering complete:', iceCandidates);
        logger.info('ICE candidates gathered', {
          host: iceCandidates.host,
          srflx: iceCandidates.srflx,
          relay: iceCandidates.relay,
          hasRelay: iceCandidates.relay > 0,
          totalCandidates: iceCandidates.host + iceCandidates.srflx + iceCandidates.relay
        });
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      
      if (pc.connectionState === 'connected') {
        setConnectionStatus('connected');
        logger.info('WebRTC connection established', {
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
          hadRelayCandidates: iceCandidates.relay > 0
        });
      } else if (pc.connectionState === 'disconnected') {
        console.log('⚠️ Connection disconnected');
        logger.warn('WebRTC connection disconnected', {
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
          hadRelayCandidates: iceCandidates.relay > 0
        });
      } else if (pc.connectionState === 'failed') {
        console.error('❌ Connection failed');
        logger.error('WebRTC connection failed - POTENTIAL TURN NEEDED', new Error('WebRTC connection failed'), {
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
          hadRelayCandidates: iceCandidates.relay > 0,
          hostCandidates: iceCandidates.host,
          srflxCandidates: iceCandidates.srflx,
          relayCandidates: iceCandidates.relay,
          noTurnServer: iceCandidates.relay === 0,
          likelyNeedsRelay: iceCandidates.relay === 0 && iceCandidates.srflx > 0
        });
      }
    };

    // Monitor ICE connection quality for adaptive video quality
    // WebRTC automatically adapts, but we can monitor it
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      console.log('ICE connection state:', state);
      
      if (state === 'connected' || state === 'completed') {
        // Get the selected candidate pair to see what connection type was used
        pc.getStats().then(stats => {
          let selectedPair = null;
          let localCandidate = null;
          let remoteCandidate = null;
          
          stats.forEach(report => {
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              selectedPair = report;
            }
          });
          
          if (selectedPair) {
            stats.forEach(report => {
              if (report.type === 'local-candidate' && report.id === selectedPair.localCandidateId) {
                localCandidate = report;
              }
              if (report.type === 'remote-candidate' && report.id === selectedPair.remoteCandidateId) {
                remoteCandidate = report;
              }
            });
          }
          
          logger.info('ICE connection established', {
            iceConnectionState: state,
            connectionState: pc.connectionState,
            hadRelayCandidates: iceCandidates.relay > 0,
            localCandidateType: localCandidate?.candidateType || 'unknown',
            remoteCandidateType: remoteCandidate?.candidateType || 'unknown',
            connectionType: localCandidate?.candidateType === 'relay' || remoteCandidate?.candidateType === 'relay' ? 'relay (TURN)' : 
                           localCandidate?.candidateType === 'srflx' || remoteCandidate?.candidateType === 'srflx' ? 'srflx (STUN)' : 'host (direct)',
            usedTurn: localCandidate?.candidateType === 'relay' || remoteCandidate?.candidateType === 'relay'
          });
        }).catch(err => {
          logger.info('ICE connection established', {
            iceConnectionState: state,
            connectionState: pc.connectionState,
            hadRelayCandidates: iceCandidates.relay > 0,
            statsError: err.message
          });
        });
      } else if (state === 'checking') {
        logger.info('ICE connection checking', {
          iceConnectionState: state,
          hostCandidates: iceCandidates.host,
          srflxCandidates: iceCandidates.srflx,
          relayCandidates: iceCandidates.relay
        });
      } else if (state === 'disconnected') {
        console.warn('⚠️ ICE connection disconnected');
        logger.warn('ICE connection disconnected', {
          iceConnectionState: state,
          hadRelayCandidates: iceCandidates.relay > 0
        });
      } else if (state === 'failed') {
        console.error('❌ ICE connection failed - This is where TURN would help!');
        logger.error('ICE connection failed - TURN SERVER NEEDED', new Error('ICE connection failed'), {
          iceConnectionState: state,
          connectionState: pc.connectionState,
          hostCandidates: iceCandidates.host,
          srflxCandidates: iceCandidates.srflx,
          relayCandidates: iceCandidates.relay,
          noTurnServer: iceCandidates.relay === 0,
          impact: iceCandidates.relay === 0 ? 'HIGH - No TURN server configured' : 'MEDIUM - TURN available but failed',
          recommendation: iceCandidates.relay === 0 ? 'Add TURN server to fix this' : 'Check TURN server configuration'
        });
      }
    };

    return pc;
  };

  // Detect in-app browser for analytics (no redirect)
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroid = /android/i.test(userAgent);
    const platform = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';
    
    // Detect in-app browsers
    const isInstagram = /instagram/i.test(userAgent);
    const isFacebook = /FBAN|FBAV|FB_IAB/i.test(userAgent);
    const isTikTok = /tiktok/i.test(userAgent);
    const isTwitter = /twitter/i.test(userAgent);
    const isLinkedIn = /linkedinapp/i.test(userAgent);
    const isSnapchat = /snapchat/i.test(userAgent);
    
    let browserType = null;
    
    if (isInstagram) {
      browserType = 'instagram';
    } else if (isFacebook) {
      browserType = 'facebook';
    } else if (isTikTok) {
      browserType = 'tiktok';
    } else if (isTwitter) {
      browserType = 'twitter';
    } else if (isLinkedIn) {
      browserType = 'linkedin';
    } else if (isSnapchat) {
      browserType = 'snapchat';
    }
    
    // Log for analytics only - no redirect
    if (browserType) {
      setDetectedInAppBrowser(browserType);
      logger.info('In-app browser detected', { 
        type: browserType, 
        platform,
        note: 'User allowed to continue in-app browser'
      });
    }
  }, []);

  useEffect(() => {
    initDatadog();
    
    // Get or create anonymous user ID
    const { aUserId, isReturningUser, minutesSinceCreation } = getOrCreateAUserId();
    
    // Set aUserId globally in Datadog - will be included in ALL logs automatically
    setUserContext(aUserId);
    
    // Log session start with retention data
    logger.info('Session started', {
      event: 'session_started',
      isReturningUser,
      minutesSinceCreation,
      timestamp: Date.now()
    });

    const socket = io(SIGNALING_SERVER_URL, {
      auth: {
        aUserId: aUserId
      },
      transports: ['websocket'], // Force WebSocket only, skip polling to avoid XHR errors
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 10000,
      upgrade: false // Don't try to upgrade from polling to websocket
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      logger.info('Connected to signaling server', { url: SIGNALING_SERVER_URL, socketId: socket.id, aUserId });
      console.log('Connected to signaling server');
      
      // Check if Start button is visible after page loads (critical for mobile!)
      setTimeout(() => {
        const startButton = document.querySelector('.start-button');
        // Only check visibility if button exists (user still on landing screen)
        if (startButton) {
          const rect = startButton.getBoundingClientRect();
          const isVisible = (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
          );
          
          logger.info('Start button visibility check', {
            event: 'start_button_visibility',
            isVisible,
            buttonTop: Math.round(rect.top),
            buttonBottom: Math.round(rect.bottom),
            windowHeight: window.innerHeight,
            scrollY: window.scrollY,
            isMobile: window.innerWidth <= 768,
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            belowFold: rect.top > window.innerHeight,
            timestamp: Date.now()
          });
          
          if (!isVisible) {
            console.warn('⚠️ START BUTTON NOT VISIBLE! Users need to scroll!');
          }
        }
        // If button not found, user likely already clicked Start (auto-join) - no need to log
      }, 3000); // Wait 3 seconds for page to settle
    });

    socket.on('connect_error', (error) => {
      console.warn('⚠️ Socket connection error:', error.message);
      logger.warn('Socket connection error', {
        event: 'socket_connect_error',
        error: error.message,
        transport: error.transport,
      });
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Attempting to reconnect to signaling server... (attempt ${attemptNumber})`);
    });

    socket.on('reconnect', (attemptNumber) => {
      logger.info('Reconnected to signaling server', { attemptNumber, socketId: socket.id });
      console.log('✅ Reconnected to signaling server after', attemptNumber, 'attempts');
    });

    socket.on('reconnect_failed', () => {
      logger.error('Failed to reconnect to signaling server', new Error('All reconnection attempts failed'), {});
      console.error('❌ Failed to reconnect to signaling server after all attempts');
    });

    socket.on('waiting', () => {
      setConnectionStatus('connecting');
      const lang = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('lookingForMatch', lang) }]);
    });

    // Handle cooldown waiting - when matched before but in cooldown
    socket.on('waiting-cooldown', (data) => {
      setConnectionStatus('connecting');
      const lang = getLanguageForCountry(selectedCountry);
      const remainingSeconds = data?.remainingSeconds || 45;
      
      logger.info('Waiting in cooldown - looking for new users', {
        event: 'waiting_cooldown',
        remainingSeconds,
        usersInQueue: data?.usersInQueue || 0
      });
      
      // Show a friendly message about looking for new users
      setChatMessages(prev => [...prev, { 
        type: 'system', 
        text: t('lookingForNewUsers', lang) || '🔄 Looking for someone new...'
      }]);
      
      // Clear any existing cooldown retry timer to avoid duplicates
      if (socket.cooldownRetryTimer) {
        clearTimeout(socket.cooldownRetryTimer);
      }
      
      // Retry find-match after cooldown expires
      const retryDelay = (remainingSeconds + 2) * 1000; // Add 2 seconds buffer
      socket.cooldownRetryTimer = setTimeout(() => {
        // Only retry if still waiting for match and not already matched
        if (socketRef.current && socketRef.current.connected && !peerConnectionRef.current) {
          logger.info('Retrying find-match after cooldown', {
            event: 'cooldown_retry',
            country: selectedCountryRef.current
          });
          socketRef.current.emit('find-match', { country: selectedCountryRef.current });
        }
      }, retryDelay);
    });

    socket.on('match-found', async (data) => {
      // Handle both old format (string) and new format (object)
      const peerId = typeof data === 'string' ? data : data.peerId;
      const isOfferer = typeof data === 'object' ? data.isOfferer : true;
      
      // Clear any pending cooldown retry timer since we got a match
      if (socket.cooldownRetryTimer) {
        clearTimeout(socket.cooldownRetryTimer);
        socket.cooldownRetryTimer = null;
      }
      
      logger.info('Match found', { peerId, isOfferer, country: selectedCountry });
      console.log('Match found:', peerId, 'isOfferer:', isOfferer);
      socket.peerId = peerId;
      setConnectionStatus('connecting');
      const lang = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('matchFound', lang) }]);

      // Create peer connection
      const pc = createPeerConnection();
      peerConnectionRef.current = pc;

      // Only the offerer creates an offer
      if (isOfferer) {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit('offer', {
            target: peerId,
            offer: pc.localDescription
          });
        } catch (err) {
          console.error('Error creating offer:', err);
        }
      }
      // The answerer will wait for the offer
    });

    socket.on('offer', async (data) => {
      console.log('Received offer');
      
      // If we already have a peer connection, reuse it (might be in wrong state)
      let pc = peerConnectionRef.current;
      
      // If no peer connection or it's in wrong state, create new one
      if (!pc || pc.signalingState === 'stable' || pc.signalingState === 'closed') {
        pc = createPeerConnection();
        peerConnectionRef.current = pc;
      }
      
      socket.peerId = data.sender;

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket.emit('answer', {
          target: data.sender,
          answer: pc.localDescription
        });
      } catch (err) {
        console.error('Error handling offer:', err);
      }
    });

    socket.on('answer', async (data) => {
      console.log('Received answer');
      if (peerConnectionRef.current) {
        try {
          // Check if we're in the right state to set remote answer
          const pc = peerConnectionRef.current;
          if (pc.signalingState === 'have-local-offer') {
            await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          } else {
            console.warn('Cannot set remote answer, wrong signaling state:', pc.signalingState);
          }
        } catch (err) {
          console.error('Error setting remote answer:', err);
        }
      }
    });

    socket.on('ice-candidate', async (data) => {
      if (peerConnectionRef.current && data.candidate) {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
      }
    });

    socket.on('peer-disconnected', () => {
      logger.warn('Peer disconnected unexpectedly', {});
      
      // Reset game state when peer disconnects
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
      setCurrentQuestion(null);
      setQuestionIndex(0);
      setPlayerScore(0);
      setPeerScore(0);
      setSelectedAnswer(null);
      selectedAnswerRef.current = null;
      setShowAnswerResult(false);
      setPeerAnswered(false);
      setBothAnswered(false);
      // Reset mask removal state and restore mask
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
      restoreMask();
      
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // ALWAYS update connection status to show loading immediately
      setConnectionStatus('connecting');
      
      // When peer disconnects (for any reason), automatically look for someone new
      // Use ref to get current country value (avoids stale closure)
      const currentCountry = selectedCountryRef.current;
      const lang = getLanguageForCountry(currentCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('peerDisconnected', lang) }]);
      
      if (socketRef.current && currentCountry) {
        console.log('🔄 Peer disconnected, automatically looking for new match with country:', currentCountry);
        socketRef.current.emit('find-match', { country: currentCountry });
      } else {
        console.warn('⚠️ Cannot auto-search: socket or country not available', {
          hasSocket: !!socketRef.current,
          country: currentCountry
        });
        // Fallback: reset to disconnected if we can't search
        setConnectionStatus('disconnected');
      }
    });

    socket.on('peer-skipped', () => {
      console.log('📨 Received peer-skipped event');
      
      // Reset game state when peer skips
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
      setCurrentQuestion(null);
      setQuestionIndex(0);
      setPlayerScore(0);
      setPeerScore(0);
      setSelectedAnswer(null);
      selectedAnswerRef.current = null;
      setShowAnswerResult(false);
      setPeerAnswered(false);
      setBothAnswered(false);
      // Reset mask removal state and restore mask
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
      restoreMask();
      
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // ALWAYS update connection status to show loading immediately
      setConnectionStatus('connecting');
      
      // When peer skips, we should also automatically look for someone new
      // Use ref to get current country value (avoids stale closure)
      const currentCountry = selectedCountryRef.current;
      const lang = getLanguageForCountry(currentCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('peerSkippedFinding', lang) }]);
      
      if (socketRef.current && currentCountry) {
        console.log('🔄 Peer skipped, automatically looking for new match with country:', currentCountry);
        socketRef.current.emit('find-match', { country: currentCountry });
      } else {
        console.warn('⚠️ Cannot auto-search: socket or country not available', {
          hasSocket: !!socketRef.current,
          country: currentCountry
        });
        // Fallback: reset to disconnected if we can't search
        setConnectionStatus('disconnected');
      }
    });

    socket.on('skip-success', () => {
      // This is handled in handleSkip() now - no need to do anything here
      // The client already started looking for a new match
    });

    socket.on('peer-stopped', () => {
      console.log('📨 Received peer-stopped event');
      
      // Reset game state when peer stops
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
      setCurrentQuestion(null);
      setQuestionIndex(0);
      setPlayerScore(0);
      setPeerScore(0);
      setSelectedAnswer(null);
      selectedAnswerRef.current = null;
      setShowAnswerResult(false);
      setPeerAnswered(false);
      setBothAnswered(false);
      // Reset mask removal state and restore mask
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
      restoreMask();
      
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // ALWAYS update connection status to show loading immediately
      setConnectionStatus('connecting');
      
      // When peer stops (closes the call), we should automatically look for someone new
      // Use ref to get current country value (avoids stale closure)
      const currentCountry = selectedCountryRef.current;
      const lang = getLanguageForCountry(currentCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('peerDisconnected', lang) }]);
      
      if (socketRef.current && currentCountry) {
        console.log('🔄 Peer stopped call, automatically looking for new match with country:', currentCountry);
        socketRef.current.emit('find-match', { country: currentCountry });
      } else {
        console.warn('⚠️ Cannot auto-search: socket or country not available', {
          hasSocket: !!socketRef.current,
          country: currentCountry
        });
        // Fallback: reset to disconnected if we can't search
        setConnectionStatus('disconnected');
      }
    });

    socket.on('chat-message', (data) => {
      setChatMessages(prev => [...prev, { type: 'peer', text: data.text }]);
      // Increment unread count if chat is closed
      setUnreadCount(prev => showChatRef.current ? 0 : prev + 1);
    });

    // Game events
    socket.on('game-request', (data) => {
      logger.info('Received game request', { peerId: socketRef.current?.peerId, gameType: data?.gameType });
      setGameState('requested');
      setGameRequestedBy('peer');
      // Store game type if provided
      if (data && data.gameType) {
        setCurrentGameType(data.gameType);
      }
    });

    socket.on('game-accepted', (data) => {
      logger.info('Game accepted', { gameType: data?.gameType, questionCount: data?.questions?.length });
      console.log('Game accepted, received data:', data);
      setGameState('active');
      setGameRequestedBy(null);
      // Store game type if provided
      if (data && data.gameType) {
        setCurrentGameType(data.gameType);
      }
      // Store questions from backend
      if (data && data.questions && data.questions.length > 0) {
        setGameQuestions(data.questions);
        gameQuestionsRef.current = data.questions; // Update ref for socket handlers
        // Start the game with questions from backend
        startGame(data.questions);
      } else {
        logger.error('No questions received from server', null, { data });
        console.error('No questions received from server:', data);
        setGameState('idle');
        const lang = getLanguageForCountry(selectedCountry);
        setChatMessages(prev => [...prev, { type: 'system', text: t('failedToStartGame', lang) }]);
      }
    });

    socket.on('game-declined', () => {
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
      const lang = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('gameRequestDeclined', lang) }]);
    });

    socket.on('game-answer', (data) => {
      // Handle peer's answer
      // Store the peer's final answer index (they may change it multiple times)
      // We'll check if it's correct when both have answered and show results
      if (data.answerIndex !== undefined) {
        peerFinalAnswerRef.current = data.answerIndex;
      }
      // Mark that peer has answered
      setPeerAnswered(true);
      
      // Check if both have answered using ref to get current value
      if (selectedAnswerRef.current !== null && !scoresUpdatedRef.current) {
        setBothAnswered(true);
        // Immediately show results when both have answered
        setShowAnswerResult(true);
        
        // Update scores based on game type
        const currentQ = gameQuestionsRef.current[questionIndexRef.current];
        const playerAnswer = selectedAnswerRef.current;
        const gameType = currentGameTypeRef.current; // Use ref to get current value
        
        if (gameType === 'quiz' && currentQ !== null) {
          // Quiz: check if answers are correct
          const isPlayerCorrect = playerAnswer === currentQ.correct;
          const isPeerCorrect = peerFinalAnswerRef.current === currentQ.correct;
          
          console.log('📊 Both answered (Quiz) - updating scores:', {
            playerAnswer,
            correctAnswer: currentQ?.correct,
            isPlayerCorrect,
            isPeerCorrect
          });
          
          scoresUpdatedRef.current = true;
          
          if (isPlayerCorrect) {
            setPlayerScore(prev => prev + 1);
          }
          if (isPeerCorrect) {
            setPeerScore(prev => prev + 1);
          }
        } else if (gameType === 'thisOrThat') {
          // This or That: both get a point if they match
          const peerAnswer = peerFinalAnswerRef.current;
          const isMatch = playerAnswer === peerAnswer;
          
          console.log('📊 Both answered (This or That) - checking match:', {
            playerAnswer,
            peerAnswer,
            isMatch
          });
          
          scoresUpdatedRef.current = true;
          
          if (isMatch) {
            // Both players get a point if they match
            setPlayerScore(prev => prev + 1);
            setPeerScore(prev => prev + 1);
          }
        }
      }
    });

    socket.on('game-ended', () => {
      setGameState('finished');
    });

    socket.on('game-cancelled', () => {
      console.log('🎮 Game cancelled by peer - resetting state');
      // Reset game state when peer cancels the game
      // Reset all game-related states immediately
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
      setCurrentQuestion(null);
      setQuestionIndex(0);
      questionIndexRef.current = 0;
      setPlayerScore(0);
      setPeerScore(0);
      setSelectedAnswer(null);
      selectedAnswerRef.current = null;
      setShowAnswerResult(false);
      setPeerAnswered(false);
      setBothAnswered(false);
      setNextQuestionCountdown(null); // Clear any countdown timers
      setGameQuestions([]);
      gameQuestionsRef.current = [];
      scoresUpdatedRef.current = false; // Reset scores updated flag
      peerFinalAnswerRef.current = null; // Reset peer's final answer
      
      // Use setTimeout to ensure state updates are processed
      setTimeout(() => {
        const lang = getLanguageForCountry(selectedCountry);
        setChatMessages(prev => [...prev, {
          type: 'system',
          text: t('peerCancelledGame', lang) || 'Peer cancelled the game'
        }]);
      }, 0);
    });

    // Mask removal events
    socket.on('mask-removal-request', () => {
      logger.info('Received mask removal request');
      setMaskRemovalState('requested');
      setMaskRemovalRequestedBy('peer');
    });

    socket.on('mask-removal-accepted', () => {
      logger.info('Mask removal accepted');
      // Store current mask before removal - use ref to avoid stale closure
      // Always store something (default to mask1 if null somehow)
      const currentMask = selectedMaskRef.current;
      previousMaskRef.current = currentMask || 'mask1';
      console.log('💾 Stored mask before removal (peer):', previousMaskRef.current);
      // Remove mask for both users
      handleMaskChange(null);
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
      setMasksRemoved(true); // Mark that masks have been removed
    });

    socket.on('mask-removal-declined', () => {
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
      const lang = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('maskRemovalRequestDeclined', lang) }]);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Load NSFW detection model with retry logic
  useEffect(() => {
    // Load model from local public folder
    // Using mobilenet_v2_mid model (size 224)
    // Note: mobilenet_v2_mid uses graph model format, not layers format
    const modelUrl = '/nsfwjs-model/mobilenet_v2_mid/';
    const maxRetries = 3;
    let retryCount = 0;
    
    const loadNsfwModel = () => {
      console.log(`Loading NSFW model from local path (attempt ${retryCount + 1}/${maxRetries}):`, modelUrl);
    
    nsfwjs.load(modelUrl, { 
      size: 224,
      type: 'graph'  // Specify graph model type
    })
      .then((model) => {
        console.log('✓ NSFW model loaded successfully');
        setNsfwModel(model);
        nsfwModelRef.current = model; // Update ref as well
        // Start detection if video is already playing
        if (remoteVideoRef.current && !remoteVideoRef.current.paused) {
          startNsfwDetection();
        }
      })
      .catch((err) => {
          retryCount++;
          
          if (retryCount < maxRetries) {
            console.warn(`⚠️ NSFW model load failed, retrying in ${retryCount * 2}s... (${retryCount}/${maxRetries})`);
            setTimeout(loadNsfwModel, retryCount * 2000); // Exponential backoff: 2s, 4s, 6s
          } else {
            console.error('✗ Error loading NSFW model after', maxRetries, 'attempts:', err);
        console.error('Make sure model files are in: web/public/nsfwjs-model/mobilenet_v2_mid/');
        console.error('Required files: model.json and group1-shard files');
            
            // Log to Datadog for monitoring
            logger.warn('NSFW model failed to load after retries - continuing without content moderation', {
              error: err.message,
              event: 'nsfw_model_load_failed',
              retries: maxRetries,
              userAgent: navigator.userAgent,
            });
            
            // App continues to work without NSFW detection
            console.log('⚠️ App will continue without NSFW content moderation');
          }
        });
    };
    
    loadNsfwModel();
  }, []);

  // Stop detection when component unmounts or connection ends
  useEffect(() => {
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, []);

  // Ensure Türkiye is set as default country on mount
  useEffect(() => {
    // Always set Türkiye as default and save to cookie
    if (selectedCountry !== 'Türkiye') {
      setSelectedCountry('Türkiye');
      setCookie('selectedCountry', 'Türkiye');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Note: Old multi-step onboarding tour removed in favor of progressive hints
  // Progressive hints are scheduled in the useEffect above when connectionStatus === 'connected'

  // Get available devices - only after camera permission is granted
  useEffect(() => {
    if (!cameraPermissionGranted) {
      return;
    }

    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        const audioDevices = devices.filter(device => device.kind === 'audioinput');
        const audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');
        
        setAvailableVideoDevices(videoDevices);
        setAvailableAudioDevices(audioDevices);
        setAvailableAudioOutputDevices(audioOutputDevices);
        
        // Set default devices
        if (videoDevices.length > 0 && !selectedVideoDevice) {
          setSelectedVideoDevice(videoDevices[0].deviceId);
        }
        if (audioDevices.length > 0 && !selectedAudioDevice) {
          setSelectedAudioDevice(audioDevices[0].deviceId);
        }
        if (audioOutputDevices.length > 0 && !selectedAudioOutputDevice) {
          setSelectedAudioOutputDevice(audioOutputDevices[0].deviceId);
        }
        
        console.log('📹 Available video devices:', videoDevices);
        console.log('🎤 Available audio devices:', audioDevices);
        console.log('🔊 Available audio output devices:', audioOutputDevices);
      } catch (err) {
        console.error('Error getting devices:', err);
      }
    };
    
    getDevices();
    
    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, [cameraPermissionGranted]);

  // Close settings popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsPopup && !event.target.closest('.settings-popup-content') && !event.target.closest('.settings-button')) {
        setShowSettingsPopup(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsPopup]);

  // Check if camera permission was already granted (from previous visit)
  useEffect(() => {
    const checkExistingPermission = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' });
          const micPermission = await navigator.permissions.query({ name: 'microphone' });
          
          if (cameraPermission.state === 'granted' && micPermission.state === 'granted') {
            console.log('📹 Camera and microphone permissions already granted, auto-initializing...');
            setCameraPermissionGranted(true);
            logger.info('Camera permission auto-granted from previous session', {
              event: 'camera_permission_auto_granted',
              timestamp: Date.now(),
            });
          }
        }
      } catch (err) {
        console.log('Could not check existing permissions:', err);
      }
    };
    
    checkExistingPermission();
  }, []); // Run once on mount

  // Initialize face filter - only after camera permission
  useEffect(() => {
    // Don't initialize camera if permission not granted
    if (!cameraPermissionGranted) {
      return;
    }

    faceDetectionListener = (isDetected) => {
      setIsFaceDetected(isDetected);
    };

    cameraReadyListener = (ready) => {
      setIsCameraReady(ready);
    };

    // Wait a bit for the canvas to be rendered in the DOM
    const timer = setTimeout(() => {
      const canvas = document.getElementById('jeeFaceFilterCanvas');
      if (canvas) {
        initJeelizAnonymous('jeeFaceFilterCanvas');
      } else {
        console.warn('Canvas not found, retrying camera initialization...');
        // Retry after a short delay if canvas is not ready
        setTimeout(() => {
          const retryCanvas = document.getElementById('jeeFaceFilterCanvas');
          if (retryCanvas) {
            initJeelizAnonymous('jeeFaceFilterCanvas');
          }
        }, 500);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      faceDetectionListener = null;
      cameraReadyListener = null;
    };
  }, [cameraPermissionGranted]);

  // Ensure mask is synced when selectedMask changes (bidirectional sync between settings and entrance screen)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.threeStuffs && selectedMask) {
      // This ensures that when mask is changed from settings, it reflects in the entrance screen and vice versa
      // handleMaskChange already handles the mask loading, but this ensures sync even if called directly
      if (ANONYMOUSOBJ3D && window.threeStuffs.faceObject) {
        // Only reload if mask is different (avoid unnecessary reloads)
        loadMask(window.threeStuffs, selectedMask);
      }
    }
  }, [selectedMask]);

  // Auto-join call after camera loads (when user first grants permission)
  useEffect(() => {
    if (shouldAutoJoin && isCameraReady && isFaceDetected && selectedCountry) {
      console.log('🎉 Camera ready, auto-joining call...');
      logger.info('Auto-joining call after camera initialization', {
        event: 'auto_join_call',
        timestamp: Date.now(),
      });
      
      // Reset auto-join flag
      setShouldAutoJoin(false);
      
      // Automatically find match
      handleFindMatch();
    }
  }, [shouldAutoJoin, isCameraReady, isFaceDetected, selectedCountry]);

  // Camera permission denial cooldown timer
  useEffect(() => {
    if (denialCooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setDenialCooldownSeconds(prev => prev - 1);
        
        // When cooldown ends, log it
        if (denialCooldownSeconds === 1) {
          logger.info('Camera denial cooldown ended', {
            event: 'camera_denial_cooldown_ended',
            timestamp: Date.now()
          });
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [denialCooldownSeconds]);

  // Track if user is idle on landing screen (hasn't clicked Start)
  useEffect(() => {
    // Only track if user hasn't granted camera permission yet (on landing screen)
    if (!cameraPermissionGranted && connectionStatus === 'disconnected') {
      // Show attention-grabbing button after 3 seconds
      const attentionTimer = setTimeout(() => {
        setShowAttentionButton(true);
        logger.info('Attention button style activated', {
          event: 'attention_button_shown',
          timestamp: Date.now()
        });
      }, 3000);

      // Show floating hint after 6 seconds
      const hintTimer = setTimeout(() => {
        setShowFloatingHint(true);
        logger.info('Floating hint shown to idle user', {
          event: 'floating_hint_shown',
          timestamp: Date.now()
        });
      }, 6000);

      const idleTimer = setTimeout(() => {
        logger.warn('User idle on landing screen for 10s without clicking Start', {
          event: 'user_idle_no_start_click',
          selectedCountry,
          startButtonEnabled: !!selectedCountry,
          timeOnPage: '10s',
          timestamp: Date.now()
        });
        console.warn('⚠️ User on page for 10s but hasn\'t clicked Start - button below fold?');
      }, 10000); // After 10 seconds

      return () => {
        clearTimeout(attentionTimer);
        clearTimeout(hintTimer);
        clearTimeout(idleTimer);
        setShowAttentionButton(false);
        setShowFloatingHint(false);
      };
    } else {
      setShowAttentionButton(false);
      setShowFloatingHint(false);
    }
  }, [cameraPermissionGranted, selectedCountry, connectionStatus]);

  // Show trivia prompt after 10 seconds of waiting for a match
  useEffect(() => {
    if (connectionStatus === 'connecting') {
      // Start tracking waiting time
      waitingStartTimeRef.current = Date.now();
      
      // Show trivia prompt after 20 seconds
      const triviaTimer = setTimeout(() => {
        if (!showTrivia) {
          setShowTriviaPrompt(true);
          logger.info('Trivia prompt shown after 20s wait', {
            event: 'trivia_prompt_shown',
            waitingDuration: '20s',
            timestamp: Date.now()
          });
        }
      }, 20000);

      return () => {
        clearTimeout(triviaTimer);
        waitingStartTimeRef.current = null;
      };
    } else {
      // Reset trivia state when not waiting
      setShowTriviaPrompt(false);
      setShowTrivia(false);
      setTriviaScore(0);
      setCurrentTriviaIndex(0);
      setTriviaAnswer(null);
    }
  }, [connectionStatus, showTrivia]);

  // Handle trivia answer
  const handleTriviaAnswer = (answerIndex) => {
    const questions = TRIVIA_QUESTIONS[currentLanguage] || TRIVIA_QUESTIONS.en;
    const currentQuestion = questions[currentTriviaIndex];
    const isCorrect = answerIndex === currentQuestion.correct;
    
    // Log trivia answer
    logger.info('Trivia answer submitted', {
      event: 'trivia_answer',
      questionIndex: currentTriviaIndex,
      isCorrect,
      currentScore: triviaScore + (isCorrect ? 1 : 0),
      timestamp: Date.now()
    });
    
    if (isCorrect) {
      setTriviaAnswer('correct');
      setTriviaScore(prev => prev + 1);
    } else {
      setTriviaAnswer('wrong');
    }

    // Move to next question after 1.5 seconds
    setTimeout(() => {
      setTriviaAnswer(null);
      setCurrentTriviaIndex(prev => (prev + 1) % questions.length);
    }, 1500);
  };

  // Start trivia game
  const startTrivia = () => {
    setShowTriviaPrompt(false);
    setShowTrivia(true);
    setTriviaScore(0);
    setCurrentTriviaIndex(Math.floor(Math.random() * 10)); // Start at random question
    logger.info('User started trivia while waiting', {
      event: 'trivia_started',
      timestamp: Date.now()
    });
  };

  // Ensure audio and video tracks are always enabled
  useEffect(() => {
    if (canvasStream) {
      canvasStream.getTracks().forEach(track => {
        // Audio and video are always enabled
        track.enabled = true;
      });
    }
    
    // Also update peer connection tracks if it exists
    if (peerConnectionRef.current) {
      peerConnectionRef.current.getSenders().forEach(sender => {
        if (sender.track) {
          sender.track.enabled = true;
        }
      });
    }
  }, []);


  const handleFindMatch = async () => {
    // Prevent action during cooldown or while requesting permission
    if (denialCooldownSeconds > 0 || isRequestingCameraPermission) {
      return;
    }
    
    // Log button click
    logger.info('Start button clicked', {
      event: 'start_button_clicked',
      cameraPermissionGranted,
      isCameraReady,
      isFaceDetected,
      selectedCountry,
        timestamp: Date.now()
      });
    
    // Request camera permission if not granted
    if (!cameraPermissionGranted) {
      // Set requesting state to disable button
      setIsRequestingCameraPermission(true);
      
      logger.info('Requesting camera permission', {
        event: 'camera_permission_request',
        timestamp: Date.now()
      });
      
    try {
      // Request camera and microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop());
      
      // Permissions granted - clear any previous denial state
      setCameraPermissionGranted(true);
      setCameraPermissionDenied(false);
      setDenialCooldownSeconds(0);
        setShouldAutoJoin(true); // Auto-join after camera loads
      
      // Log to Datadog
      logger.info('User granted camera and microphone access', {
        event: 'camera_permission_granted',
          timestamp: Date.now(),
      });
          
        // Don't proceed with finding match - let camera initialize first
        // Will auto-join when camera becomes ready
        setIsRequestingCameraPermission(false);
        return;
    } catch (err) {
      // Permission denied or error
      console.error('Camera permission error:', err);
      setIsRequestingCameraPermission(false);
        
        // Log the failure with details
        logger.error('Camera permission denied or failed', err, {
          event: 'camera_permission_denied',
          errorName: err.name,
          errorMessage: err.message,
          timestamp: Date.now(),
          impact: 'User cannot proceed to video chat'
        });
        
        // Set denial state and start cooldown
        setCameraPermissionDenied(true);
        setDenialCooldownSeconds(10); // 10 second cooldown
        
        logger.info('Camera denial cooldown started', {
          event: 'camera_denial_cooldown_started',
          cooldownSeconds: 10,
          timestamp: Date.now()
        });
        
      return;
      }
    }
    
    // Block finding match if face/mask is not detected
    if (!isFaceDetected) {
      console.warn('Cannot find match: Face/mask not detected');
      logger.warn('Start blocked: Face/mask not detected', { 
        event: 'start_blocked_no_face',
        isFaceDetected,
        isCameraReady,
        selectedCountry,
        impact: 'User blocked from starting chat'
      });
      setChatMessages(prev => [...prev, { 
        type: 'system', 
        text: t('faceRequired', currentLanguage) 
      }]);
      return;
    }
    
    // Block finding match if country is not selected
    if (!selectedCountry) {
      console.warn('Cannot find match: Country not selected');
      logger.warn('Start blocked: Country not selected', { 
        event: 'start_blocked_no_country',
        selectedCountry,
        isFaceDetected,
        isCameraReady,
        impact: 'User blocked from starting chat'
      });
      setChatMessages(prev => [...prev, { 
        type: 'system', 
        text: t('countryRequired', currentLanguage) 
      }]);
      return;
    }

    // Send Google Ads conversion event when user successfully tries to join a call
    if (typeof window !== 'undefined' && typeof window.gtag_report_conversion === 'function') {
      window.gtag_report_conversion();
      console.log('📊 Google Ads conversion event sent: User joining call');
    }

    if (socketRef.current) {
      logger.info('Finding match', { country: selectedCountry });
      // Log to Datadog when user tries to join a call
      logger.info('User attempting to join a call', {
        event: 'user_join_call_attempt',
        country: selectedCountry,
        timestamp: Date.now(),
      });
      setChatMessages([]);
      setConnectionStatus('connecting');
      socketRef.current.emit('find-match', { country: selectedCountry });
    }
  };

  const handleSkip = () => {
    if (socketRef.current && connectionStatus === 'connected') {
      logger.info('Skipping peer', { peerId: socketRef.current.peerId });
      // Reset game state immediately
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
      setCurrentQuestion(null);
      setQuestionIndex(0);
      setPlayerScore(0);
      setPeerScore(0);
      setSelectedAnswer(null);
      selectedAnswerRef.current = null;
      setShowAnswerResult(false);
      setPeerAnswered(false);
      setBothAnswered(false);
      // Reset mask removal state and restore mask
      setMaskRemovalState('idle');
      setMaskRemovalRequestedBy(null);
      restoreMask();
      
      // Close peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // Clear remote video
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
      
      // Emit skip to server
      socketRef.current.emit('skip');
      
      // Immediately start looking for new match
      setConnectionStatus('connecting');
      const lang = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('skippedFindingNew', lang) }]);
      
      // Start searching for new match
      socketRef.current.emit('find-match', { country: selectedCountry });
    }
  };

  const handleStop = () => {
    // Notify server that we're stopping (not skipping)
    // The peer should auto-search, but we should go to initial screen
    if (socketRef.current && connectionStatus !== 'disconnected') {
      logger.info('Stopping call', { peerId: socketRef.current.peerId });
      socketRef.current.emit('stop-call');
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Clear remote video
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    
    // Reset connection status
    setConnectionStatus('disconnected');
    
    // Reset game state
    setGameState('idle');
    setGameRequestedBy(null);
    setCurrentGameType(null);
    setCurrentQuestion(null);
    setQuestionIndex(0);
    setPlayerScore(0);
    setPeerScore(0);
    setSelectedAnswer(null);
    selectedAnswerRef.current = null;
    setShowAnswerResult(false);
    setPeerAnswered(false);
    setBothAnswered(false);
    // Reset mask removal state and restore mask
    setMaskRemovalState('idle');
    setMaskRemovalRequestedBy(null);
    restoreMask();
    
    // Clear chat messages
    setChatMessages([]);
    setUnreadCount(0);
    
    console.log('🛑 Call stopped, returning to initial screen');
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (chatInput.trim() && socketRef.current && socketRef.current.peerId) {
      const message = chatInput.trim();
      setChatMessages(prev => [...prev, { type: 'own', text: message }]);
      socketRef.current.emit('chat-message', { text: message });
      setChatInput('');
    }
  };

  // Game handlers
  // Available game types - use useMemo to recalculate when language changes
  const gameTypes = useMemo(() => [
    { 
      id: 'quiz', 
      name: t('gameQuiz', currentLanguage), 
      icon: '🎯', 
      description: t('gameQuizDescription', currentLanguage) 
    },
    { 
      id: 'thisOrThat', 
      name: t('gameThisOrThat', currentLanguage), 
      icon: '⚖️', 
      description: t('gameThisOrThatDescription', currentLanguage) 
    }
  ], [currentLanguage]);

  const handleRequestGame = () => {
    // Show game selection menu instead of directly requesting
    setShowGameSelection(true);
  };

  const handleSelectGame = (gameType) => {
    setShowGameSelection(false);
    setCurrentGameType(gameType.id); // Store the selected game type
    if (socketRef.current && socketRef.current.peerId) {
      logger.info('Sending game request', { gameType: gameType.id, peerId: socketRef.current.peerId });
      socketRef.current.emit('game-request', { 
        target: socketRef.current.peerId,
        gameType: gameType.id 
      });
      setGameState('requested');
      setGameRequestedBy('self');
      const lang = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, { type: 'system', text: t('gameRequestSent', lang) }]);
    }
  };

  const handleAcceptGame = () => {
    if (socketRef.current && socketRef.current.peerId) {
      logger.info('Accepting game request', { gameType: currentGameType, peerId: socketRef.current.peerId });
      socketRef.current.emit('game-accept', { 
        target: socketRef.current.peerId,
        gameType: currentGameType || 'quiz' // Pass game type to server
      });
      // Don't call startGame() here - wait for server to send questions via 'game-accepted' event
      // The game will start automatically when questions are received
    }
  };

  const handleDeclineGame = () => {
    if (socketRef.current && socketRef.current.peerId) {
      logger.info('Declining game request', { peerId: socketRef.current.peerId });
      socketRef.current.emit('game-decline', { target: socketRef.current.peerId });
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
    }
  };

  const startGame = (questions) => {
    if (!questions || questions.length === 0) {
      console.error('No questions provided to start game');
      return;
    }
    setQuestionIndex(0);
    questionIndexRef.current = 0; // Update ref
    setPlayerScore(0);
    setPeerScore(0);
    setSelectedAnswer(null);
    selectedAnswerRef.current = null; // Reset ref
    peerFinalAnswerRef.current = null; // Reset peer's final answer
    scoresUpdatedRef.current = false; // Reset scores updated flag
    setShowAnswerResult(false);
    setPeerAnswered(false);
    setBothAnswered(false);
    setCurrentQuestion(questions[0]);
  };

  const handleAnswer = (answerIndex) => {
    // Allow changing answer until both players have answered
    if (bothAnswered) return;
    
    setSelectedAnswer(answerIndex);
    selectedAnswerRef.current = answerIndex; // Update ref
    
    // Don't show result yet - wait for both to answer
    // Don't update score yet - wait for both to answer

    // Send answer to peer
    if (socketRef.current && socketRef.current.peerId) {
      // For quiz: check if answer is correct
      // For thisOrThat: just send the answer index (matching logic handled later)
      const isCorrect = currentGameType === 'quiz' 
        ? answerIndex === currentQuestion.correct 
        : null; // For thisOrThat, we'll check matching instead
      
      socketRef.current.emit('game-answer', {
        target: socketRef.current.peerId,
        correct: isCorrect,
        answerIndex: answerIndex, // Send the actual answer index for thisOrThat
        questionIndex: questionIndex
      });
    }

    // Check if peer has already answered (using state setter to get current value)
    setPeerAnswered(prev => {
      if (prev && !scoresUpdatedRef.current) {
        // Peer already answered, so both have answered now
        setBothAnswered(true);
        setShowAnswerResult(true);
        
        // Update scores based on game type
        if (currentGameType === 'quiz') {
          // Quiz: check if answers are correct
          const isPlayerCorrect = answerIndex === currentQuestion.correct;
          const isPeerCorrect = peerFinalAnswerRef.current === currentQuestion.correct;
          
          console.log('📊 Both answered (Quiz) - updating scores:', {
            isPlayerCorrect,
            isPeerCorrect,
            questionIndex
          });
          
          scoresUpdatedRef.current = true;
          
          if (isPlayerCorrect) {
            setPlayerScore(prevScore => prevScore + 1);
          }
          if (isPeerCorrect) {
            setPeerScore(prevScore => prevScore + 1);
          }
        } else if (currentGameType === 'thisOrThat') {
          // This or That: both get a point if they match
          const peerAnswer = peerFinalAnswerRef.current;
          const isMatch = answerIndex === peerAnswer;
          
          console.log('📊 Both answered (This or That) - checking match:', {
            playerAnswer: answerIndex,
            peerAnswer,
            isMatch
          });
          
          scoresUpdatedRef.current = true;
          
          if (isMatch) {
            // Both players get a point if they match
            setPlayerScore(prevScore => prevScore + 1);
            setPeerScore(prevScore => prevScore + 1);
          }
        }
      }
      return prev;
    });
  };

  // Synchronize bothAnswered state when both players have answered
  // When both have answered, show results and update score
  // This useEffect handles the case when we answer AFTER the peer has already answered
  useEffect(() => {
    if (selectedAnswer !== null && peerAnswered && !bothAnswered && gameState === 'active' && !scoresUpdatedRef.current) {
      console.log('✅ Both answered (we answered after peer) - showing results immediately', {
        selectedAnswer,
        correctAnswer: currentQuestion?.correct,
        bothAnswered,
        showAnswerResult
      });
      setBothAnswered(true);
      // Now show the answer result immediately - no delay
      setShowAnswerResult(true);
      
      // Update scores based on game type
      if (currentGameType === 'quiz') {
        // Quiz: check if answers are correct
        const isPlayerCorrect = selectedAnswer === currentQuestion?.correct;
        const isPeerCorrect = peerFinalAnswerRef.current === currentQuestion?.correct;
        
        console.log('📊 Both answered in useEffect (Quiz) - updating scores:', {
          isPlayerCorrect,
          isPeerCorrect
        });
        
        scoresUpdatedRef.current = true;
        
        if (isPlayerCorrect) {
          setPlayerScore(prev => prev + 1);
        }
        if (isPeerCorrect) {
          setPeerScore(prev => prev + 1);
        }
      } else if (currentGameType === 'thisOrThat') {
        // This or That: both get a point if they match
        const peerAnswer = peerFinalAnswerRef.current;
        const isMatch = selectedAnswer === peerAnswer;
        
        console.log('📊 Both answered in useEffect (This or That) - checking match:', {
          playerAnswer: selectedAnswer,
          peerAnswer,
          isMatch
        });
        
        scoresUpdatedRef.current = true;
        
        if (isMatch) {
          // Both players get a point if they match
          setPlayerScore(prev => prev + 1);
          setPeerScore(prev => prev + 1);
        }
      }
    }
  }, [selectedAnswer, peerAnswered, bothAnswered, gameState, currentQuestion, currentGameType]);

  // Move to next question when both players have answered
  useEffect(() => {
    if (bothAnswered && gameState === 'active' && gameQuestions.length > 0) {
      // Start countdown from 3
      setNextQuestionCountdown(3);
      
      // Update countdown every second
      const countdownInterval = setInterval(() => {
        setNextQuestionCountdown(prev => {
          if (prev === null || prev <= 1) {
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Wait 3 seconds to allow players to see results, then move to next question
      const timer = setTimeout(() => {
        setNextQuestionCountdown(null); // Clear countdown
        if (questionIndex < gameQuestions.length - 1) {
          const nextIndex = questionIndex + 1;
          // Reset all answer-related states before moving to next question
          setSelectedAnswer(null);
          selectedAnswerRef.current = null; // Reset ref
          peerFinalAnswerRef.current = null; // Reset peer's final answer for new question
          scoresUpdatedRef.current = false; // Reset scores updated flag for new question
          setShowAnswerResult(false);
          setPeerAnswered(false);
          setBothAnswered(false);
          // Then update question
          setQuestionIndex(nextIndex);
          questionIndexRef.current = nextIndex; // Update ref
          setCurrentQuestion(gameQuestions[nextIndex]);
        } else {
          // Game finished
          endGame();
        }
      }, 3000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
        setNextQuestionCountdown(null);
      };
    }
  }, [bothAnswered, questionIndex, gameQuestions, gameState]);

  const endGame = () => {
    logger.info('Game finished', { playerScore, peerScore });
    setGameState('finished');
    if (socketRef.current && socketRef.current.peerId) {
      socketRef.current.emit('game-end', { target: socketRef.current.peerId });
    }
    setChatMessages(prev => [...prev, {
      type: 'system',
      text: t('gameFinishedScore', currentLanguage, { playerScore, peerScore })
    }]);
  };

  const handleCancelGame = () => {
    const lang = getLanguageForCountry(selectedCountry);
    const confirmMessage = t('cancelGameConfirm', lang) || 'Are you sure you want to end the game? This will end the game for both players.';
    if (window.confirm(confirmMessage)) {
      // Reset game state
      setGameState('idle');
      setGameRequestedBy(null);
      setCurrentGameType(null);
      setCurrentQuestion(null);
      setQuestionIndex(0);
      questionIndexRef.current = 0;
      setPlayerScore(0);
      setPeerScore(0);
      setSelectedAnswer(null);
      selectedAnswerRef.current = null;
      setShowAnswerResult(false);
      setPeerAnswered(false);
      setBothAnswered(false);
      setNextQuestionCountdown(null); // Clear any countdown timers
      setGameQuestions([]);
      gameQuestionsRef.current = [];
      scoresUpdatedRef.current = false; // Reset scores updated flag
      peerFinalAnswerRef.current = null; // Reset peer's final answer
      
      // Notify server to cancel game for both players
      if (socketRef.current && socketRef.current.peerId) {
        logger.info('Cancelling game', { peerId: socketRef.current.peerId });
        socketRef.current.emit('game-cancel', { target: socketRef.current.peerId });
      }
      
      const lang2 = getLanguageForCountry(selectedCountry);
      setChatMessages(prev => [...prev, {
        type: 'system',
        text: t('gameCancelled', lang2) || 'Game cancelled'
      }]);
    }
  };

  // Update meta tags dynamically for SEO (React 19 compatible - using native DOM API)
  useEffect(() => {
    // Update title
    document.title = 'AnonMasque - Random Video Chat with Face Masks';
    
    // Helper function to update or create meta tags
    const updateMetaTag = (property, content, isProperty = false) => {
      const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Update description
    updateMetaTag('description', 'Anonymous random video chat with face masks. Connect with strangers worldwide using 3D masks like Anonymous, Venetian, and Catwoman. Private, secure, and fun video chatting experience.');
    
    // Update Open Graph tags
    updateMetaTag('og:title', 'AnonMasque - Random Video Chat with Face Masks', true);
    updateMetaTag('og:description', 'Connect with strangers worldwide using 3D face masks. Anonymous, secure, and fun random video chat experience.', true);
    
    // Update Twitter tags
    updateMetaTag('twitter:title', 'AnonMasque - Random Video Chat with Face Masks');
    updateMetaTag('twitter:description', 'Connect with strangers worldwide using 3D face masks. Anonymous, secure, and fun random video chat experience.');
  }, []);

  return (
    <div className="App">
      {/* FAQ Button - Top Right (Oval) */}
      {connectionStatus === 'disconnected' && !cameraPermissionGranted && (
        <button 
          className="faq-button-top-right"
          onClick={() => setShowFAQ(!showFAQ)}
          aria-expanded={showFAQ}
          aria-label={showFAQ ? t('hideFAQ', currentLanguage) || 'Hide FAQ' : t('showFAQ', currentLanguage) || 'Show FAQ'}
        >
          {showFAQ ? t('hideFAQ', currentLanguage) || 'Hide FAQ' : t('showFAQ', currentLanguage) || 'Show FAQ'}
        </button>
      )}

      {/* FAQ Content Overlay */}
      {showFAQ && connectionStatus === 'disconnected' && !cameraPermissionGranted && (
        <div className="faq-overlay" onClick={() => setShowFAQ(false)}>
          <div className="faq-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="faq-close-button"
              onClick={() => setShowFAQ(false)}
              aria-label={t('close', currentLanguage) || 'Close'}
            >
              ×
            </button>
            <h3 className="faq-title">{t('faqTitle', currentLanguage) || 'Frequently Asked Questions'}</h3>
            <div className="faq-list">
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ1', currentLanguage) || 'What is AnonMasque?'}</h4>
                <p className="faq-answer">{t('faqA1', currentLanguage) || 'AnonMasque is an anonymous random video chat platform where you can connect with strangers worldwide while wearing fun 3D face masks like Anonymous, Venetian, and Catwoman masks.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ2', currentLanguage) || 'Is it really anonymous?'}</h4>
                <p className="faq-answer">{t('faqA2', currentLanguage) || 'Yes! We don\'t store your personal information, and you can chat anonymously while wearing masks. Your privacy is our priority.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ3', currentLanguage) || 'How do the masks work?'}</h4>
                <p className="faq-answer">{t('faqA3', currentLanguage) || 'Our advanced face detection technology applies 3D masks to your face in real-time. You can choose from different mask styles and even remove masks during calls if both users agree.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ4', currentLanguage) || 'Is it free to use?'}</h4>
                <p className="faq-answer">{t('faqA4', currentLanguage) || 'Yes, AnonMasque is completely free to use. Just select your country and start chatting!'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ5', currentLanguage) || 'Can I skip users?'}</h4>
                <p className="faq-answer">{t('faqA5', currentLanguage) || 'Yes, you can skip to the next person at any time. You can also play games together or chat via text messages.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ6', currentLanguage) || 'What devices are supported?'}</h4>
                <p className="faq-answer">{t('faqA6', currentLanguage) || 'AnonMasque works on desktop and mobile browsers. For the best experience, use Chrome, Safari, or Firefox with camera access enabled.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ7', currentLanguage) || 'What games can I play with strangers?'}</h4>
                <p className="faq-answer">{t('faqA7', currentLanguage) || 'You can play fun games like Quiz (trivia questions) and This or That (choice-based games) with strangers during your video call. Games help break the ice and make conversations more engaging!'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ8', currentLanguage) || 'How do I video call strangers online?'}</h4>
                <p className="faq-answer">{t('faqA8', currentLanguage) || 'Simply select your country, click "Start", and we\'ll match you with a random stranger for a video call. You can chat via video, text messages, or play games together while wearing fun masks!'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ9', currentLanguage) || 'Is it safe to meet strangers online?'}</h4>
                <p className="faq-answer">{t('faqA9', currentLanguage) || 'Yes! AnonMasque prioritizes your safety with automatic content moderation, anonymous connections, and the ability to skip users at any time. You remain anonymous and can disconnect whenever you want.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ10', currentLanguage) || 'Can I chat with strangers via text messages?'}</h4>
                <p className="faq-answer">{t('faqA10', currentLanguage) || 'Yes! In addition to video calling strangers, you can send text messages during your call. This is perfect for when you want to communicate without speaking or share links and information.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ11', currentLanguage) || 'How do I change masks during a video call?'}</h4>
                <p className="faq-answer">{t('faqA11', currentLanguage) || 'Click the settings icon and go to the Masks tab. You can switch between Anonymous, Venetian, and Catwoman masks anytime. You can also request to remove masks if both users agree.'}</p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">{t('faqQ12', currentLanguage) || 'What countries can I connect with?'}</h4>
                <p className="faq-answer">{t('faqA12', currentLanguage) || 'You can meet strangers from any country! Select your preferred country when starting, and we\'ll match you with users from that region. This helps you connect with people from specific locations or cultures.'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main video chat interface */}
        <div className="video-chat-container">
          {/* Site name */}
          <div className="site-name">
            <img src="/logo.png" alt="AnonMasque" className="site-name-icon" loading="lazy" />
            <span>AnonMasque</span>
          </div>
        {/* Remote video */}
        <div className="remote-video-container">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className={`remote-video ${nsfwDetected ? 'nsfw-blocked' : ''}`}
            style={nsfwDetected ? { filter: 'brightness(0)' } : {}}
          />
          {nsfwDetected && (
            <div className="nsfw-warning-overlay">
              <div className="nsfw-warning-content">
                <div className="nsfw-icon">⚠️</div>
                <h2>{t('inappropriateContent', currentLanguage)}</h2>
                <p>{t('nudityDetected', currentLanguage)}</p>
                <p>{t('videoBlocked', currentLanguage)}</p>
                <button className="skip-button" onClick={handleSkip}>
                  {t('skipThisUser', currentLanguage)}
                </button>
              </div>
            </div>
          )}
          {connectionStatus === 'disconnected' && (
            <div className="connection-overlay">
              <div className="connection-message">
                {!cameraPermissionGranted ? (
                  <>
                    <h2>{t('readyToChat', currentLanguage)}</h2>
                    <p className="entrance-subtitle">{t('entranceSubtitle', currentLanguage)}</p>
                    
                    {/* Preview masks in a compact horizontal row */}
                    <div className="entrance-mask-preview">
                      {Object.keys(MASK_CONFIGS).slice(0, 4).map((maskId) => (
                        <div key={maskId} className="entrance-mask-preview-item">
                          <img 
                            src={MASK_CONFIGS[maskId].image} 
                            alt={MASK_CONFIGS[maskId].displayName}
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                    
                    <div className="country-selector">
                      <select
                        id="country-select"
                        className="country-select"
                        value={selectedCountry || ''}
                        onChange={(e) => {
                          const newCountry = e.target.value;
                          setSelectedCountry(newCountry);
                          // Save to cookie
                          setCookie('selectedCountry', newCountry);
                          console.log('🍪 Saved country to cookie:', newCountry);
                        }}
                      >
                        {COUNTRIES.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="start-button-container">
                      {showFloatingHint && !cameraPermissionDenied && !isRequestingCameraPermission && (
                        <div className="floating-hint">
                          <span className="hint-text">👆 {t('tapToStart', currentLanguage) || 'Tap to start chatting!'}</span>
                          <div className="hint-arrow"></div>
                        </div>
                      )}
                      
                      {/* Camera Permission Denied Message */}
                      {cameraPermissionDenied && (
                        <div className="camera-denied-message">
                          <div className="denied-icon">📷</div>
                          <p className="denied-title">{t('cameraNeeded', currentLanguage) || 'Camera access needed'}</p>
                          <p className="denied-instructions">
                            {detectedInAppBrowser ? (
                              t('cameraInstructionsInApp', currentLanguage) || 'Open in Safari/Chrome: Tap ⋮ or ⋯ menu → "Open in Browser"'
                            ) : (
                              t('cameraInstructions', currentLanguage) || 'Tap the 🔒 in your browser address bar → Allow camera access'
                            )}
                          </p>
                          {detectedInAppBrowser && (
                            <div className="open-browser-buttons">
                    <button 
                                className="open-in-browser-btn"
                                onClick={() => {
                                  const url = window.location.href;
                                  // Try to open in external browser
                                  // For Android, try intent URL first
                                  const isAndroid = /android/i.test(navigator.userAgent);
                                  if (isAndroid) {
                                    // Android intent to open in Chrome or default browser
                                    window.location.href = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
                                  } else {
                                    // iOS - window.open might work, or show copy option
                                    window.open(url, '_system');
                                  }
                                  logger.info('Open in browser button clicked', {
                                    event: 'open_in_browser_clicked',
                                    browser: detectedInAppBrowser,
                                    timestamp: Date.now()
                                  });
                                }}
                              >
                                🌐 {t('openInBrowser', currentLanguage) || 'Open in Browser'}
                              </button>
                              <button 
                                className="copy-link-btn"
                                onClick={() => {
                                  navigator.clipboard.writeText(window.location.href).then(() => {
                                    // Show brief success feedback
                                    const btn = document.querySelector('.copy-link-btn');
                                    if (btn) {
                                      btn.textContent = '✓ ' + (t('copied', currentLanguage) || 'Copied!');
                                      setTimeout(() => {
                                        btn.textContent = '📋 ' + (t('copyLink', currentLanguage) || 'Copy Link');
                                      }, 2000);
                                    }
                                  });
                                  logger.info('Copy link button clicked', {
                                    event: 'copy_link_clicked',
                                    browser: detectedInAppBrowser,
                                    timestamp: Date.now()
                                  });
                                }}
                              >
                                📋 {t('copyLink', currentLanguage) || 'Copy Link'}
                              </button>
                            </div>
                          )}
                          {denialCooldownSeconds > 0 && (
                            <p className="denied-cooldown">
                              {t('tryAgainIn', currentLanguage) || 'Try again in'} {denialCooldownSeconds}s
                            </p>
                          )}
                        </div>
                      )}
                      
                      <button 
                        className={`start-button ${showAttentionButton && !cameraPermissionDenied ? 'attention-pulse' : ''} ${isRequestingCameraPermission ? 'requesting' : ''} ${denialCooldownSeconds > 0 ? 'cooldown' : ''}`}
                      onClick={handleFindMatch}
                        disabled={!selectedCountry || isRequestingCameraPermission || denialCooldownSeconds > 0}
                      >
                        {isRequestingCameraPermission ? (
                          <span className="button-loading">
                            <span className="button-spinner"></span>
                            {t('requesting', currentLanguage) || 'Requesting...'}
                          </span>
                        ) : denialCooldownSeconds > 0 ? (
                          `${t('waitSeconds', currentLanguage) || 'Wait'} ${denialCooldownSeconds}s`
                        ) : (
                          t('start', currentLanguage)
                        )}
                    </button>
                    </div>
                  </>
                ) : !isCameraReady ? (
                  <>
                    <div className="spinner"></div>
                    <h2>{t('loadingCamera', currentLanguage)}</h2>
                    <p>{t('initializingCamera', currentLanguage) || 'Initializing camera... Please wait'}</p>
                  </>
                ) : (
                  <>
                    <h2>{t('readyToChat', currentLanguage)}</h2>
                    
                    <div className="country-selector">
                      <select
                        id="country-select"
                        className="country-select"
                        value={selectedCountry || ''}
                        onChange={(e) => {
                          const newCountry = e.target.value;
                          setSelectedCountry(newCountry);
                          // Save to cookie
                          setCookie('selectedCountry', newCountry);
                          console.log('🍪 Saved country to cookie:', newCountry);
                        }}
                      >
                        {COUNTRIES.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Mask selector */}
                    <div className="entrance-mask-selector">
                      <div className="entrance-masks-grid">
                        {Object.keys(MASK_CONFIGS).map((maskId) => (
                          <div
                            key={maskId}
                            className={`entrance-mask-option ${selectedMask === maskId ? 'selected' : ''}`}
                            onClick={() => handleMaskChange(maskId)}
                            title={MASK_CONFIGS[maskId].displayName}
                          >
                            <div className="entrance-mask-image-container">
                              {maskImageLoading[maskId] && (
                                <div className="mask-image-loading">
                                  <div className="mask-loading-spinner"></div>
                                </div>
                              )}
                              <img 
                                src={MASK_CONFIGS[maskId].image} 
                                alt={MASK_CONFIGS[maskId].displayName}
                                className={`entrance-mask-image ${maskImageLoading[maskId] ? 'loading' : 'loaded'}`}
                                loading="lazy"
                                onLoad={() => {
                                  setMaskImageLoading(prev => ({ ...prev, [maskId]: false }));
                                }}
                                onError={() => {
                                  setMaskImageLoading(prev => ({ ...prev, [maskId]: false }));
                                }}
                              />
                            </div>
                            <div className="entrance-mask-name">
                              {MASK_CONFIGS[maskId].displayName}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {!isFaceDetected && (
                      <div className="face-detection-message">
                        <p>{t('faceNotDetected', currentLanguage)}</p>
                      </div>
                    )}
                    
                    <button 
                      className={`start-button ${isCameraReady ? 'camera-ready' : ''}`}
                      onClick={handleFindMatch}
                      disabled={!selectedCountry || !isFaceDetected}
                    >
                      {t('start', currentLanguage)}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          {connectionStatus === 'connecting' && (
            <div className="connection-overlay waiting-overlay">
              <div className="waiting-content">
                {/* Header with waiting message */}
                <div className="waiting-header">
                  <div className="spinner"></div>
                  <p className="waiting-message">{t('lookingForSomeone', currentLanguage)}</p>
                </div>

                {/* Trivia Game */}
                {showTrivia && (
                  <div className="trivia-container">
                    <button 
                      className="trivia-close-button" 
                      onClick={() => {
                        setShowTrivia(false);
                        setShowTriviaPrompt(false);
                        logger.info('User closed trivia game', {
                          event: 'trivia_closed',
                          score: triviaScore,
                          questionsAnswered: currentTriviaIndex,
                          timestamp: Date.now()
                        });
                      }}
                      aria-label="Close trivia"
                    >
                      ✕
                    </button>
                    <div className="trivia-header">
                      <span className="trivia-score">🎯 {triviaScore}</span>
                      <span className="trivia-label">{t('triviaGame', currentLanguage) || 'Trivia'}</span>
                    </div>
                    <div className="trivia-question">
                      {(TRIVIA_QUESTIONS[currentLanguage] || TRIVIA_QUESTIONS.en)[currentTriviaIndex]?.question}
                    </div>
                    <div className="trivia-options">
                      {(TRIVIA_QUESTIONS[currentLanguage] || TRIVIA_QUESTIONS.en)[currentTriviaIndex]?.options.map((option, idx) => (
                        <button
                          key={idx}
                          className={`trivia-option ${
                            triviaAnswer !== null
                              ? idx === (TRIVIA_QUESTIONS[currentLanguage] || TRIVIA_QUESTIONS.en)[currentTriviaIndex].correct
                                ? 'correct'
                                : triviaAnswer === 'wrong' && idx === (TRIVIA_QUESTIONS[currentLanguage] || TRIVIA_QUESTIONS.en)[currentTriviaIndex].options.indexOf(option)
                                  ? 'wrong'
                                  : ''
                              : ''
                          }`}
                          onClick={() => triviaAnswer === null && handleTriviaAnswer(idx)}
                          disabled={triviaAnswer !== null}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trivia Prompt */}
                {showTriviaPrompt && !showTrivia && (
                  <div className="trivia-prompt">
                    <button 
                      className="trivia-prompt-dismiss" 
                      onClick={() => {
                        setShowTriviaPrompt(false);
                        logger.info('User dismissed trivia prompt', {
                          event: 'trivia_prompt_dismissed',
                          timestamp: Date.now()
                        });
                      }}
                      aria-label="Dismiss"
                    >
                      ✕
                    </button>
                    <div className="trivia-prompt-content">
                      <span className="trivia-prompt-icon">🎮</span>
                      <p>{t('boredWaiting', currentLanguage) || 'Bored waiting?'}</p>
                      <button className="trivia-start-button" onClick={startTrivia}>
                        {t('playTrivia', currentLanguage) || "Let's Play!"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Mask selector while waiting */}
                <div className="waiting-mask-selector">
                  <p className="waiting-mask-hint">{t('tryDifferentMasks', currentLanguage) || 'Try different masks!'}</p>
                  <div className="waiting-masks-grid">
                    {Object.keys(MASK_CONFIGS).map((maskId) => (
                      <div
                        key={maskId}
                        className={`waiting-mask-option ${selectedMask === maskId ? 'selected' : ''}`}
                        onClick={() => handleMaskChange(maskId)}
                        title={MASK_CONFIGS[maskId].displayName}
                      >
                        <img 
                          src={MASK_CONFIGS[maskId].image} 
                          alt={MASK_CONFIGS[maskId].displayName}
                          className="waiting-mask-image"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Local video with mask */}
        <div className={`local-video-container ${connectionStatus === 'disconnected' ? 'centered' : ''} ${connectionStatus === 'connecting' ? 'fullscreen-preview' : ''}`}>
        <div className="jee-container">
          {!cameraPermissionGranted && (
            <div className="camera-placeholder-background">
              <img src="/img/background_anonmasque.jpg" alt="" loading="lazy" />
            </div>
          )}
          <canvas
            id="jeeFaceFilterCanvas"
            width="500"
            height="500"
          />
          {!isFaceDetected && connectionStatus !== 'disconnected' && (
            <div className="no-face-message">{t('noFaceDetected', currentLanguage)}</div>
          )}
        </div>
          {connectionStatus !== 'disconnected' && (
            <div className="local-video-label">{t('you', currentLanguage)}</div>
          )}
        </div>

        {/* Settings button - Entrance screen */}
        {connectionStatus === 'disconnected' && isCameraReady && (
          <button
            className="settings-button settings-button-entrance"
            onClick={() => setShowSettingsPopup(true)}
            title="Settings"
          >
            <MdSettings size={24} />
          </button>
        )}

        {/* Controls */}
        {(connectionStatus === 'connected' || connectionStatus === 'connecting') && (
          <div className="controls">
            <>
              {/* Settings button */}
              <button
                className="control-button settings-button"
                onClick={() => setShowSettingsPopup(true)}
                title="Settings"
              >
                <MdSettings size={24} />
              </button>

              <button
                className="control-button skip-button"
                onClick={connectionStatus === 'connected' ? handleSkip : undefined}
                disabled={connectionStatus !== 'connected'}
                title={
                  connectionStatus !== 'connected'
                    ? t('waitingForMatch', currentLanguage) || 'Waiting for match...'
                    : t('skipToNext', currentLanguage)
                }
              >
                <MdSkipNext size={28} /> <span className="button-text">{t('skip', currentLanguage)}</span>
              </button>
              <button
                className="control-button stop-button"
                onClick={handleStop}
                title={t('stopCall', currentLanguage) || 'Stop Call'}
              >
                <MdCallEnd size={28} /><span className="button-text">{t('stop', currentLanguage) || 'Stop'}</span>
              </button>
              <button
                className={`control-button game-button ${
                  gameState === 'requested' && gameRequestedBy === 'self' 
                    ? 'game-button-waiting' 
                    : gameState === 'active' 
                    ? 'game-button-active' 
                    : ''
                }`}
                onClick={gameState === 'idle' && connectionStatus === 'connected' ? handleRequestGame : undefined}
                disabled={gameState !== 'idle' || connectionStatus !== 'connected'}
                title={
                  connectionStatus !== 'connected'
                    ? t('waitingForMatch', currentLanguage) || 'Waiting for match...'
                    : gameState === 'requested' && gameRequestedBy === 'self'
                    ? t('gameRequestSent', currentLanguage)
                    : gameState === 'active'
                    ? t('gameInProgress', currentLanguage) || 'Game in progress'
                    : t('requestGame', currentLanguage)
                }
              >
                {gameState === 'requested' && gameRequestedBy === 'self' ? (
                  <div className="game-button-spinner"></div>
                ) : (
                  <IoGameController size={26} />
                )}
                <span className="button-text">
                  {gameState === 'requested' && gameRequestedBy === 'self' 
                    ? t('waiting', currentLanguage) || 'Waiting...' 
                    : gameState === 'active' 
                    ? t('gameActive', currentLanguage) || 'Game Active' 
                    : t('game', currentLanguage)}
                </span>
              </button>
              <button
                className={`control-button mask-removal-button ${
                  maskRemovalState === 'requested' && maskRemovalRequestedBy === 'self' 
                    ? 'mask-removal-button-waiting' 
                    : masksRemoved
                    ? 'mask-removal-button-disabled'
                    : ''
                }`}
                onClick={maskRemovalState === 'idle' && connectionStatus === 'connected' && !masksRemoved ? handleRequestMaskRemoval : undefined}
                disabled={maskRemovalState !== 'idle' || connectionStatus !== 'connected' || masksRemoved}
                title={
                  connectionStatus !== 'connected'
                    ? t('waitingForMatch', currentLanguage) || 'Waiting for match...'
                    : masksRemoved
                    ? t('masksAlreadyRemoved', currentLanguage) || 'Masks have already been removed'
                    : maskRemovalState === 'requested' && maskRemovalRequestedBy === 'self'
                    ? t('maskRemovalRequestSent', currentLanguage)
                    : t('requestMaskRemoval', currentLanguage)
                }
              >
                {maskRemovalState === 'requested' && maskRemovalRequestedBy === 'self' ? (
                  <div className="game-button-spinner"></div>
                ) : (
                  <MdRemoveCircle size={26} />
                )}
                <span className="button-text">
                  {maskRemovalState === 'requested' && maskRemovalRequestedBy === 'self' 
                    ? t('waiting', currentLanguage) || 'Waiting...' 
                    : t('removeMask', currentLanguage)}
                </span>
              </button>
              <button
                className={`control-button ${showChat ? 'active' : ''} ${unreadCount > 0 ? 'has-unread' : ''}`}
                onClick={() => {
                  if (connectionStatus === 'connected') {
                    setShowChat(!showChat);
                    // Clear unread count when opening chat
                    if (!showChat) {
                      setUnreadCount(0);
                    }
                  }
                }}
                disabled={connectionStatus !== 'connected'}
                title={
                  connectionStatus !== 'connected'
                    ? t('waitingForMatch', currentLanguage) || 'Waiting for match...'
                    : showChat 
                    ? t('closeChat', currentLanguage) 
                    : t('toggleChat', currentLanguage)
                }
              >
                <IoChatboxEllipsesOutline size={26} />
                <span className="button-text">{t('chat', currentLanguage)}</span>
                {unreadCount > 0 && (
                  <span className="chat-notification-badge">{unreadCount}</span>
                )}
              </button>
            </>
          </div>
        )}

        {/* Game Selection Menu */}
        {showGameSelection && (
          <div className="game-selection-overlay" onClick={() => setShowGameSelection(false)}>
            <div className="game-selection-content" onClick={(e) => e.stopPropagation()}>
              <button 
                className="game-selection-close"
                onClick={() => setShowGameSelection(false)}
                title={t('close', currentLanguage)}
              >
                ✕
              </button>
              <h3>🎮 {t('selectGame', currentLanguage) || 'Select a Game'}</h3>
              <div className="game-options">
                {gameTypes.map((game) => (
                  <button
                    key={game.id}
                    className="game-option-button"
                    onClick={() => handleSelectGame(game)}
                  >
                    <span className="game-option-icon">{game.icon}</span>
                    <div className="game-option-info">
                      <div className="game-option-name">{game.name}</div>
                      <div className="game-option-description">{game.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Game UI */}
        {gameState === 'requested' && gameRequestedBy === 'peer' && (
          <div className="game-request-overlay">
            <div className="game-request-content">
              <h3>🎮 {t('gameRequest', currentLanguage)}</h3>
              <p>
                {currentGameType === 'thisOrThat' 
                  ? t('peerWantsToPlayThisOrThat', currentLanguage)
                  : t('peerWantsToPlay', currentLanguage)
                }
              </p>
              <div className="game-request-buttons">
                <button className="game-accept-button" onClick={handleAcceptGame}>
                  {t('accept', currentLanguage)}
                </button>
                <button className="game-decline-button" onClick={handleDeclineGame}>
                  {t('decline', currentLanguage)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mask Removal Request UI */}
        {maskRemovalState === 'requested' && maskRemovalRequestedBy === 'peer' && (
          <div className="game-request-overlay">
            <div className="game-request-content">
              <h3>🎭 {t('maskRemovalRequest', currentLanguage)}</h3>
              <p>{t('peerWantsToRemoveMasks', currentLanguage)}</p>
              <div className="game-request-buttons">
                <button className="game-accept-button" onClick={handleAcceptMaskRemoval}>
                  {t('accept', currentLanguage)}
                </button>
                <button className="game-decline-button" onClick={handleDeclineMaskRemoval}>
                  {t('decline', currentLanguage)}
                </button>
              </div>
            </div>
          </div>
        )}

        {gameState === 'active' && currentQuestion && (
          <div className="game-overlay">
            <div className="game-content">
              <div className="game-header">
                {/* Scores are hidden during the game, shown only at the end */}
                <div className="game-question-number">
                  {t('question', currentLanguage)} {questionIndex + 1} / {gameQuestions.length}
                </div>
                <button
                  className="game-cancel-button"
                  onClick={handleCancelGame}
                  title={t('cancelGame', currentLanguage) || 'Cancel Game'}
                >
                  ✕
                </button>
              </div>
              <div className="game-question">
                <h3>{currentQuestion.question}</h3>
                <div className="game-waiting-message-container">
                  {selectedAnswer !== null && !peerAnswered && (
                    <p className="game-waiting-message">{t('waitingForPeer', currentLanguage)}</p>
                  )}
                  {peerAnswered && selectedAnswer === null && (
                    <p className="game-waiting-message">{t('peerAnswered', currentLanguage)}</p>
                  )}
                </div>
              </div>
              {/* Show countdown when both answered - positioned absolutely to avoid layout shift */}
              {bothAnswered && nextQuestionCountdown !== null && (
                <div className="game-next-question-indicator">
                  <div className="countdown-circle">
                    <svg className="countdown-svg" viewBox="0 0 50 50">
                      <circle
                        className="countdown-circle-bg"
                        cx="25"
                        cy="25"
                        r="20"
                      />
                      <circle
                        className="countdown-circle-progress"
                        cx="25"
                        cy="25"
                        r="20"
                        style={{
                          strokeDashoffset: `${(1 - (nextQuestionCountdown / 3)) * 125.6}`
                        }}
                      />
                    </svg>
                    <span className="countdown-number">{nextQuestionCountdown}</span>
                  </div>
                  <p className="countdown-text">
                    {questionIndex < gameQuestions.length - 1 
                      ? t('nextQuestion', currentLanguage) || 'Next question...'
                      : t('finishing', currentLanguage) || 'Finishing...'}
                  </p>
                </div>
              )}
              <div className="game-options" key={`question-options-${questionIndex}`}>
                {currentQuestion.options.map((option, index) => {
                  let optionClass = 'game-option';
                  const isSelected = selectedAnswer === index;
                  
                  // For quiz: check if this is the correct answer
                  // For thisOrThat: check if both players matched on this option
                  const isCorrect = currentGameType === 'quiz' 
                    ? index === currentQuestion.correct 
                    : false; // For thisOrThat, we'll show match differently
                  
                  const isPeerSelected = currentGameType === 'thisOrThat' 
                    && bothAnswered 
                    && showAnswerResult 
                    && peerFinalAnswerRef.current === index;
                  
                  const isMatch = currentGameType === 'thisOrThat' 
                    && bothAnswered 
                    && showAnswerResult 
                    && isSelected 
                    && isPeerSelected;
                  
                  // Show correct answer when both have answered (for quiz)
                  if (currentGameType === 'quiz' && bothAnswered && showAnswerResult && isCorrect) {
                    optionClass += ' correct';
                  }
                  
                  // Show match highlight for thisOrThat
                  if (currentGameType === 'thisOrThat' && bothAnswered && showAnswerResult && isMatch) {
                    optionClass += ' correct'; // Use same styling for matches
                  }
                  
                  // Handle selected answer styling
                  if (isSelected) {
                    if (bothAnswered && showAnswerResult) {
                      // For quiz: show incorrect if wrong
                      if (currentGameType === 'quiz' && !isCorrect) {
                        optionClass += ' incorrect';
                      }
                      // For thisOrThat: show if it matched or not
                      if (currentGameType === 'thisOrThat' && !isMatch) {
                        optionClass += ' incorrect'; // Show as incorrect if didn't match
                      }
                    } else {
                      // Just show as selected until both answer
                      optionClass += ' selected';
                    }
                  }
                  
                  // For thisOrThat, also highlight peer's selection
                  if (currentGameType === 'thisOrThat' && isPeerSelected && !isSelected && bothAnswered && showAnswerResult) {
                    optionClass += ' peer-selected';
                  }
                  
                  // Show peer icon when peer selected this option (for both quiz and thisOrThat games)
                  const showPeerIcon = (currentGameType === 'quiz' || currentGameType === 'thisOrThat')
                    && bothAnswered 
                    && showAnswerResult 
                    && peerFinalAnswerRef.current === index;
                  
                  return (
                    <button
                      key={`question-${questionIndex}-option-${index}`}
                      className={optionClass}
                      onClick={() => handleAnswer(index)}
                      disabled={bothAnswered}
                    >
                      <span className="game-option-text">{option}</span>
                      {showPeerIcon && (
                        <MdPerson className="game-option-peer-icon" size={18} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {gameState === 'finished' && (
          <div className="game-overlay">
            <div className="game-content game-finished">
              <h2>🎮 {t('gameFinished', currentLanguage)}</h2>
              {currentGameType === 'quiz' && (
                <div style={{ margin: '20px 0' }}>
                  {/* Show win/loss/draw result */}
                  {playerScore > peerScore && (
                    <p style={{ color: '#4CAF50', margin: '0 0 10px 0', fontSize: '20px', fontWeight: '700' }}>
                      {t('youWon', currentLanguage) || '🎉 You Won!'}
                    </p>
                  )}
                  {playerScore < peerScore && (
                    <p style={{ color: '#f44336', margin: '0 0 10px 0', fontSize: '20px', fontWeight: '700' }}>
                      {t('youLost', currentLanguage) || '😔 You Lost'}
                    </p>
                  )}
                  {playerScore === peerScore && (
                    <p style={{ color: '#FFC107', margin: '0 0 10px 0', fontSize: '20px', fontWeight: '700' }}>
                      {t('itsADraw', currentLanguage) || '🤝 It\'s a Draw!'}
                    </p>
                  )}
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '0 0 10px 0', fontSize: '18px', fontWeight: '600' }}>
                    {t('quizScoreSummary', currentLanguage, { 
                      playerScore: playerScore,
                      peerScore: peerScore
                    }) || `You: ${playerScore} | Peer: ${peerScore}`}
                  </p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '0', fontSize: '14px' }}>
                    {t('thanksForPlaying', currentLanguage) || 'Thanks for playing!'}
                  </p>
                </div>
              )}
              {currentGameType === 'thisOrThat' && (
                <div style={{ margin: '20px 0' }}>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '0 0 10px 0', fontSize: '18px', fontWeight: '600' }}>
                    {t('matchesSummary', currentLanguage, { 
                      count: playerScore, 
                      plural: playerScore !== 1 ? 's' : '' 
                    }) || `You matched ${playerScore} time${playerScore !== 1 ? 's' : ''}!`}
                  </p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '0', fontSize: '14px' }}>
                    {t('thanksForPlaying', currentLanguage) || 'Thanks for playing!'}
                  </p>
                </div>
              )}
              <button className="game-close-button" onClick={() => {
                setGameState('idle');
                setCurrentGameType(null);
              }}>
                {t('close', currentLanguage)}
              </button>
            </div>
          </div>
        )}

        {/* Chat panel */}
        {showChat && (
          <div className="chat-panel">
            <button
              className="chat-close-button"
              onClick={() => setShowChat(false)}
              title={t('closeChat', currentLanguage)}
            >
              ✕
            </button>
            <div className="chat-messages">
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`chat-message ${msg.type}`}>
                  {msg.text}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <form className="chat-input-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                className="chat-input"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={t('typeMessage', currentLanguage)}
              />
              <button type="submit" className="chat-send-button">
                {t('send', currentLanguage)}
              </button>
            </form>
          </div>
        )}

        {/* Settings Popup */}
        {showSettingsPopup && (
          <div className="settings-popup-overlay" onClick={() => setShowSettingsPopup(false)}>
            <div className="settings-popup-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="settings-close-button"
                onClick={() => setShowSettingsPopup(false)}
                title={t('close', currentLanguage)}
              >
                ✕
              </button>
              <h2 className="settings-title">{t('settings', currentLanguage)}</h2>
              <div className="settings-container">
                {/* Left sidebar with tabs */}
                <div className="settings-sidebar">
                  <button
                    className={`settings-tab ${settingsTab === 'masks' ? 'active' : ''}`}
                    onClick={() => setSettingsTab('masks')}
                  >
                    <MdFilterList size={20} />
                    <span>{t('masks', currentLanguage)}</span>
                  </button>
                  <button
                    className={`settings-tab ${settingsTab === 'audio' ? 'active' : ''}`}
                    onClick={() => setSettingsTab('audio')}
                  >
                    <MdMic size={20} />
                    <span>{t('audio', currentLanguage)}</span>
                  </button>
                  <button
                    className={`settings-tab ${settingsTab === 'video' ? 'active' : ''}`}
                    onClick={() => setSettingsTab('video')}
                  >
                    <MdVideocam size={20} />
                    <span>{t('video', currentLanguage)}</span>
                  </button>
                </div>

                {/* Right content area */}
                <div className="settings-content">
                  {settingsTab === 'masks' && (
                    <div className="settings-section">
                      <h3 className="settings-section-title">{t('faceMasks', currentLanguage)}</h3>
                      {masksRemoved ? (
                        <div style={{ 
                          padding: '20px', 
                          textAlign: 'center', 
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: '14px'
                        }}>
                          {t('masksRemovedMessage', currentLanguage) || 'Masks have been removed for this call. You can change masks again after the call ends.'}
                        </div>
                      ) : (
                        <div className="filters-grid">
                          {Object.keys(MASK_CONFIGS).map((maskId) => (
                            <div
                              key={maskId}
                              className={`filter-box ${selectedMask === maskId ? 'selected' : ''}`}
                              onClick={() => handleMaskChange(maskId)}
                              title={MASK_CONFIGS[maskId].displayName}
                            >
                              <div className="filter-image-container">
                                {maskImageLoading[maskId] && (
                                  <div className="mask-image-loading">
                                    <div className="mask-loading-spinner"></div>
                                  </div>
                                )}
                                <img 
                                  src={MASK_CONFIGS[maskId].image} 
                                  alt={MASK_CONFIGS[maskId].displayName}
                                  className={`filter-image ${maskImageLoading[maskId] ? 'loading' : 'loaded'}`}
                                  loading="lazy"
                                  onLoad={() => {
                                    setMaskImageLoading(prev => ({ ...prev, [maskId]: false }));
                                  }}
                                  onError={() => {
                                    setMaskImageLoading(prev => ({ ...prev, [maskId]: false }));
                                  }}
                                />
                              </div>
                              <div className="filter-label">
                                {MASK_CONFIGS[maskId].displayName}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {settingsTab === 'audio' && (
                    <>
                      <div className="settings-section">
                        <h3 className="settings-section-title">{t('microphone', currentLanguage)}</h3>
                        <div className="settings-device-selector">
                          <div className="settings-device-icon">
                            <MdMic size={20} />
                          </div>
                          <select
                            className="settings-select"
                            value={selectedAudioDevice || ''}
                            onChange={(e) => setSelectedAudioDevice(e.target.value)}
                            disabled
                          >
                            {availableAudioDevices.map((device) => (
                              <option key={device.deviceId} value={device.deviceId}>
                                {device.label || `${t('microphone', currentLanguage)} ${availableAudioDevices.indexOf(device) + 1}`}
                              </option>
                            ))}
                          </select>
                          <div className="settings-dropdown-icon">
                            <MdArrowDropDown size={20} />
                          </div>
                        </div>
                      </div>

                      <div className="settings-section">
                        <h3 className="settings-section-title">{t('speaker', currentLanguage)}</h3>
                        <div className="settings-device-selector">
                          <div className="settings-device-icon">
                            <MdVolumeUp size={20} />
                          </div>
                          <select
                            className="settings-select"
                            value={selectedAudioOutputDevice || ''}
                            onChange={(e) => setSelectedAudioOutputDevice(e.target.value)}
                            disabled
                          >
                            {availableAudioOutputDevices.map((device) => (
                              <option key={device.deviceId} value={device.deviceId}>
                                {device.label || `${t('speaker', currentLanguage)} ${availableAudioOutputDevices.indexOf(device) + 1}`}
                              </option>
                            ))}
                          </select>
                          <div className="settings-dropdown-icon">
                            <MdArrowDropDown size={20} />
                          </div>
                        </div>
                      </div>

                      <div className="settings-section">
                        <h3 className="settings-section-title">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <MdGraphicEq size={20} />
                            {t('noiseCancellation', currentLanguage)}
                          </div>
                        </h3>
                        <label className="settings-toggle">
                          <input
                            type="checkbox"
                            checked={noiseCancellationEnabled}
                            onChange={toggleNoiseCancellation}
                          />
                          <div className="toggle-slider"></div>
                          <span className="settings-toggle-text">
                            {noiseCancellationEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {settingsTab === 'video' && (
                    <div className="settings-section">
                      <h3 className="settings-section-title">{t('camera', currentLanguage)}</h3>
                      <div className="settings-device-selector">
                        <div className="settings-device-icon">
                          <MdVideocam size={20} />
                        </div>
                        <select
                          className="settings-select"
                          value={selectedVideoDevice || ''}
                          onChange={(e) => setSelectedVideoDevice(e.target.value)}
                          disabled
                        >
                          {availableVideoDevices.map((device) => (
                            <option key={device.deviceId} value={device.deviceId}>
                              {device.label || `${t('camera', currentLanguage)} ${availableVideoDevices.indexOf(device) + 1}`}
                            </option>
                          ))}
                        </select>
                        <div className="settings-dropdown-icon">
                          <MdArrowDropDown size={20} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Progressive Hints - Non-blocking contextual tips */}
      {activeHint && hintPosition && (
        <div 
          className="progressive-hint"
          onClick={() => dismissHint(activeHint.id)}
              style={{
                position: 'fixed',
            top: `${hintPosition.top - 60}px`,
            left: `${Math.max(140, Math.min(hintPosition.left, window.innerWidth - 140))}px`,
                transform: 'translateX(-50%)',
                zIndex: 10000,
          }}
        >
          <div className="progressive-hint-content">
            <span className="progressive-hint-emoji">{activeHint.emoji}</span>
            <span className="progressive-hint-text">{activeHint.content}</span>
            <button className="progressive-hint-dismiss" onClick={(e) => { e.stopPropagation(); dismissHint(activeHint.id); }}>×</button>
                  </div>
          <div className="progressive-hint-arrow" />
          <div className="progressive-hint-progress" />
        </div>
      )}
    </div>
  );
}

export default App;
